// src/components/PatientDetails/panels/DataPanel.tsx
// -----------------------------------------------------------------------------
// DataPanel - Left panel displaying patient info and extracted tables
// -----------------------------------------------------------------------------
// Features:
// - Anonymized patient name display (J*** D***)
// - Patient ID for reference
// - Universal tables (Patient Profile, Molecular Profile)
// - Cancer-specific tables
// - Collapsible sections
// - Independent scrolling
// -----------------------------------------------------------------------------

import { Add, Badge, Description, Fingerprint, LocalHospital, Person, RefreshOutlined, ErrorOutline, OpenInNew, Science, EditOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import ExternalIdEditModal from "../ExternalIdEditModal";
import PatientEnvironmentsModal from "../PatientEnvironmentsModal";
import { athenaColors } from "../../../theme/palette";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { createData, fetchData, updateData } from "api/apiHelpers";
import api from "api/api";
import { useNotification } from "hooks/useNotification";
import { useFieldGroups } from "hooks/useFieldGroups";
import { useFieldSchema } from "hooks/useFieldSchema";
import { useTranslation } from "../../../i18n";
import AddClinicalDataButton from "../../editable/AddClinicalDataButton";
import EditableTable, { PER_FIELD_PROVENANCE_KINDS, EVENT_STYLE_UNIVERSAL_KINDS } from "../../editable/EditableTable";
import FullFieldModal from "../../editable/FullFieldModal";
import ExpandToggle from "../../layout/ExpandToggle";
import GlassBox from "../../layout/GlassBox";
import AnatomicalBodyMap from "../charts/AnatomicalBodyMap";
import EligibleTrialsPanel from "./EligibleTrialsPanel";

// Lazy-load the timeline — only fetched when user hovers the patient name
const PatientJourneyTimeline = lazy(
  () => import("../charts/PatientJourneyTimeline")
);

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type DomainType = { value: string; label: string };

/** Row of extracted clinical data — keyed by field name, value varies by domain schema */
type TableRow = Record<string, string | number | boolean | null | undefined>;

type DomainTablesData = {
  event: TableRow[];
  static: TableRow[];
};

type UniversalTablesData = {
  patientProfile: TableRow[];
  molecularProfile: TableRow[];
  cancerProfile: TableRow[];
  cancerObservation: TableRow[];
  hematologicalProfile: TableRow[];
  trialEnrollment: TableRow[];
  adverseEvents: TableRow[];
  medications: TableRow[];
  diagnoses: TableRow[];
  procedures: TableRow[];
  tumorAssessments: TableRow[];
  diseaseStatuses: TableRow[];
  imaging: TableRow[];
  systemicTherapies: TableRow[];
  labResults: TableRow[];
  familyHistory: TableRow[];
};

const MAX_TABLE_HEIGHT = 600;

const EMPTY_ROWS: TableRow[] = [];

/** Narrow 3px scrollbar styles — reused across all scrollable table wrappers */
const NARROW_SCROLLBAR_SX = {
  "&::-webkit-scrollbar": { width: 3, height: 3 },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "2px",
    marginBottom: "2px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(120,120,140,0.18)",
    borderRadius: 2,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(120,120,140,0.3)",
  },
  scrollbarWidth: "thin" as const,
  scrollbarColor: "rgba(120,120,140,0.18) transparent",
};

interface DataPanelProps {
  /** Patient ID */
  patientId?: string | number;
  /** Anonymized patient name (J*** D***) */
  anonymizedName?: string;
  /** Full patient name (for internal use) */
  fullName?: string;
  /** Environment names the patient belongs to */
  environmentNames?: string[];
  patientUpdatedAt?: string;
  patientDomains?: string[];
  /** Whether this caller may reveal the raw name/external_id (from PatientSerializer). */
  canRevealPii?: boolean;
  /** Institution's external identifier (MRN, study ID) */
  externalId?: string;
  /** Legacy cohort ID for cross-import matching */
  legacyId?: string;
  /** Available domain types */
  domainTypes: DomainType[];
  /** Currently selected domains */
  selectedDomains: string[];
  /** Tables data from backend */
  tablesData: {
    domains: { [key: string]: DomainTablesData };
    universal: UniversalTablesData;
  };
  /** Loading state */
  loading?: boolean;
  /** Callback when tables data changes */
  onTablesDataChange?: (
    domain: string,
    type: "event" | "static",
    data: TableRow[]
  ) => void;
  /** Callback to add a domain to the active selection (auto-activate from (+) button) */
  onDomainAdd?: (domainKey: string) => void;
  /** Callback when a new clinical data record is created (triggers refresh) */
  onRecordCreated?: () => void;
  /** True only when the AI extraction pipeline is actively running (not during data refreshes) */
  isAIExtracting?: boolean;
  /** Whether extraction appears stale/stuck (polling timed out) */
  isStaleProcessing?: boolean;
  /** Whether any clinical tables have processing_status="error" (C1) */
  hasExtractionErrors?: boolean;
  /** Handler to unstick + retry extraction for this patient */
  onRetryExtraction?: () => Promise<boolean>;
  /** Section ID to scroll to (from sidebar navigation) */
  scrollToSection?: string;
  /** Table keys to force visible (e.g. after sidebar (+) creation) */
  externalForceVisibleTables?: Set<string>;
  /** Inline action buttons rendered in the nav chip bar (sidebar layout) */
  inlineActions?: React.ReactNode;
  /** When true, tables get 3x the default max height (sidebar layout has more space) */
  sidebarMode?: boolean;
  /** Sidebar-driven record focus, routed to a single event table by `key`
   *  (a domain's `${domain}_event`, or "medications"/"adverse_events"). `section`
   *  deep-links to a field-group; `nonce` re-triggers on re-selection. */
  eventFocus?: {
    key: string;
    id: string | number;
    section?: string;
    nonce: number;
  } | null;
  /** Action buttons rendered inside the patient header card (bottom-right) */
  headerActions?: React.ReactNode;
  /** Handler for generate report (featured action in header card) */
  onGenerateReport?: () => Promise<void>;
  /** Category modal request from sidebar "+" button */
  categoryModal?: { sectionId: string; categoryName: string } | null;
  /** Close the category modal */
  onCloseCategoryModal?: () => void;
  /** Notify parent when the user expands a section from the main content (for sidebar highlight sync) */
  onActiveSectionChange?: (sectionId: string) => void;
}

// -----------------------------------------------------------------------------
// Helper: Anonymize Name
// -----------------------------------------------------------------------------

export const anonymizeName = (name: string): string => {
  if (!name) return "Unknown Patient"; // Note: anonymizeName is called outside component, can't use t() here
  // Normalise underscores to spaces so "Ana_Pereira" → "A** P******"
  const parts = name.trim().replace(/_/g, " ").split(/\s+/);
  return parts
    .map((part) =>
      part.length > 0 ? `${part[0]}${"*".repeat(part.length - 1)}` : ""
    )
    .join(" ");
};

// -----------------------------------------------------------------------------
// Patient Header Card — embeds body map + journey timeline inside one card
// Hides visualizations on small panels (< 350px width) and small devices
// -----------------------------------------------------------------------------

const MIN_VIZ_WIDTH = 350; // px — below this, hide visualizations

interface HeaderCardProps {
  displayName: string;
  patientId?: string | number;
  environmentNames?: string[];
  patientUpdatedAt?: string;
  patientDomains?: string[];
  externalId?: string;
  /** Whether this caller may reveal the raw name/external_id of this patient. */
  canRevealPii?: boolean;
  legacyId?: string;
  tablesData: {
    domains: { [key: string]: DomainTablesData };
    universal: UniversalTablesData;
  };
  hasEventData: boolean;
  hasLocationData: boolean;
  /** Small utility action buttons (link, share, export, delete) */
  actionButtons?: React.ReactNode;
  /** Featured AI action buttons (Find Trials, Generate Report) — rendered prominently on right */
  featuredActions?: React.ReactNode;
  /** When true (tables area scrolled), render a shorter header: smaller avatar
   *  + tighter vertical padding, handing more vertical space to the tables. */
  compact?: boolean;
}

const PatientHeaderCard: React.FC<HeaderCardProps> = ({
  displayName,
  patientId,
  environmentNames,
  patientUpdatedAt,
  patientDomains,
  externalId,
  canRevealPii,
  legacyId,
  tablesData,
  hasEventData,
  hasLocationData,
  actionButtons,
  featuredActions,
  compact = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const notification = useNotification();
  // External ID inline editor (header). `extOverride` shows the freshly-saved
  // value immediately without refetching the whole patient.
  const [editExtOpen, setEditExtOpen] = useState(false);
  const [extOverride, setExtOverride] = useState<string | null>(null);
  // PII reveal (eye icon next to the name). The patient API now serves the
  // anonymised `name` and `external_id` to unauthorised callers; clicking the
  // eye fetches the raw values from /patients/<id>/reveal-name/ — a per-patient
  // endpoint that writes a PatientNameReveal audit row on every call.
  const [revealed, setRevealed] = useState<{ name: string; extId: string } | null>(null);
  const [revealing, setRevealing] = useState(false);
  const effectiveExternalId = extOverride ?? revealed?.extId ?? externalId;
  const effectiveDisplayName = revealed?.name || displayName;

  const handleReveal = useCallback(async () => {
    if (!patientId || revealing) return;
    setRevealing(true);
    try {
      const resp = await api.get<{ name: string; external_id: string }>(
        `/patients/${patientId}/reveal-name/`,
      );
      setRevealed({ name: resp.data.name || "", extId: resp.data.external_id || "" });
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      notification.error(
        status === 403
          ? t(
              "patient.reveal_forbidden",
              "You don't have permission to view this patient's raw name.",
            )
          : t(
              "patient.reveal_failed",
              "Could not reveal the name — the server returned an error.",
            ),
      );
    } finally {
      setRevealing(false);
    }
  }, [patientId, revealing, notification, t]);
  // Environment membership editor (header). `envOverride` reflects the new env
  // names immediately after a change without refetching the whole patient.
  const [editEnvOpen, setEditEnvOpen] = useState(false);
  const [envOverride, setEnvOverride] = useState<string[] | null>(null);
  const effectiveEnvNames = envOverride ?? environmentNames;
  // Track container width — hide visualizations when column is too narrow
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(400);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCardWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const showViz = cardWidth >= MIN_VIZ_WIDTH;
  /** Domain keys are "cancer.breast"; the chip wants "Breast Cancer". */
  const domainChips = useMemo(() => {
    // patientDomains first: the header card's own tablesData.domains is empty
    // (its body map draws the generic silhouette for the same reason), so
    // reading it here produced no chips at all.
    const keys =
      patientDomains && patientDomains.length > 0
        ? patientDomains
        : Object.keys(tablesData.domains || {});
    return keys.map((k) => {
      const leaf = k.split(".").pop() || k;
      const pretty = leaf
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return /cancer/i.test(pretty) ? pretty : `${pretty} Cancer`;
    });
  }, [patientDomains, tablesData.domains]);

  // Navbar-style palette for the patient card
  const cardBg = `linear-gradient(180deg, ${alpha(
    theme.palette.primary.main,
    0.95
  )} 0%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`;
  const lightText = theme.palette.secondary.main; // periwinkle — same as navbar icons/text

  // ── Timeline hover state (lazy-loaded on first hover) ──
  const nameRef = useRef<HTMLDivElement>(null);
  const [timelineHover, setTimelineHover] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false); // stays true after first hover
  const hoverEnterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverLeaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNameEnter = useCallback(() => {
    if (!hasEventData || !showViz) return;
    // Cancel any pending leave
    if (hoverLeaveTimeout.current) clearTimeout(hoverLeaveTimeout.current);
    hoverEnterTimeout.current = setTimeout(() => {
      setTimelineHover(true);
      setTimelineLoaded(true); // keep loaded forever after first hover
    }, 250); // small delay to avoid flicker on accidental mouse-overs
  }, [hasEventData, showViz]);

  const handleNameLeave = useCallback(() => {
    if (hoverEnterTimeout.current) clearTimeout(hoverEnterTimeout.current);
    // Delay closing so user can move mouse to the popper
    hoverLeaveTimeout.current = setTimeout(() => {
      setTimelineHover(false);
    }, 300);
  }, []);

  // Re-enter: user moved mouse into the popper (keeps it open)
  const handlePopperEnter = useCallback(() => {
    if (hoverLeaveTimeout.current) clearTimeout(hoverLeaveTimeout.current);
  }, []);

  // Leave popper: close after delay
  const handlePopperLeave = useCallback(() => {
    hoverLeaveTimeout.current = setTimeout(() => {
      setTimelineHover(false);
    }, 200);
  }, []);

  return (
    <Box
      sx={{
        flexShrink: 0,
        m: 0,
        // Persistent gap below the (fixed) header card so the space above the
        // first table matches the space below the last table (~12px) and stays
        // consistent whether or not the header is compact/scrolled. (The compact
        // state still shrinks the card's own padding + avatar, just not this gap.)
        mb: 1.5,
        px: 1.5,
        // Tighter vertical padding overall; shrinks further once the tables
        // are scrolled (compact) to give the scroll area more height.
        py: compact ? 0.75 : 1.25,
        transition: "padding 0.2s ease",
        // Uniform 20px radius (= the sidebar inner card's borderRadius 2.5) on
        // ALL corners. The lone bottom border is dropped so it reads as a
        // free-floating rounded card (the sidebar card has none); a full subtle
        // border + the existing shadow give it definition.
        borderRadius: 2.5,
        background: cardBg,
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.dark, 0.2)}`,
      }}
    >
      <Box ref={cardRef} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Left column: avatar + name */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {/* Avatar + name row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: showViz && !compact ? 56 : 40,
                height: showViz && !compact ? 56 : 40,
                transition: "width 0.2s ease, height 0.2s ease",
                borderRadius: "50%",
                background: alpha(theme.palette.primary.dark, 0.3),
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                boxShadow:
                  theme.custom?.glassShadow ||
                  `0 8px 32px ${alpha(theme.palette.primary.dark, 0.25)}`,
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {showViz ? (
                <AnatomicalBodyMap
                  domains={tablesData.domains || {}}
                  universal={tablesData.universal}
                  hideLegend
                  compact
                />
              ) : (
                <Person
                  sx={{
                    color: theme.palette.secondary.main,
                    fontSize: 22,
                  }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Typography
                  ref={nameRef}
                  variant="h6"
                  onMouseEnter={handleNameEnter}
                  onMouseLeave={handleNameLeave}
                  sx={{
                    // No flex:1 — keep the name at its natural width so the eye
                    // sits immediately to its right. Shrinks to fit + truncates
                    // for very long names.
                    flex: "0 1 auto",
                    minWidth: 0,
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontFamily: "monospace",
                    color: theme.palette.common.white,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.3,
                    cursor: hasEventData && showViz ? "default" : undefined,
                  }}
                >
                  {effectiveDisplayName}
                </Typography>
                {/* Reveal eye — only for callers permitted by the backend (PatientSerializer.can_reveal_pii).
                    Each click hits /patients/<id>/reveal-name/ which writes a PatientNameReveal audit row.
                    Clicking again toggles back to the anonymised form. */}
                {canRevealPii && (
                  <Tooltip
                    title={
                      revealed
                        ? t("patient.hide_name_tooltip", "Hide raw name")
                        : t("patient.reveal_name_tooltip", "Reveal raw name (audit-logged)")
                    }
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={revealed ? () => setRevealed(null) : handleReveal}
                      disabled={revealing}
                      aria-label={
                        revealed
                          ? t("patient.hide_name_tooltip", "Hide raw name")
                          : t("patient.reveal_name_tooltip", "Reveal raw name (audit-logged)")
                      }
                      sx={{
                        p: 0.25,
                        color: alpha(theme.palette.common.white, 0.6),
                        flexShrink: 0,
                        "&:hover": {
                          color: theme.palette.common.white,
                          backgroundColor: alpha(theme.palette.common.white, 0.12),
                        },
                      }}
                    >
                      {revealing ? (
                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                      ) : revealed ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(lightText, 0.7),
                    fontWeight: 500,
                  }}
                >
                  ID: {patientId || "---"}
                </Typography>
                {legacyId && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: alpha(lightText, 0.4) }}
                    >
                      ·
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha(lightText, 0.7),
                        fontWeight: 500,
                      }}
                    >
                      Legacy: {legacyId}
                    </Typography>
                  </>
                )}
                {/* External ID — always shown (with an em dash when empty) so
                    the edit affordance is available even before any ID is set.
                    Supports one or several comma-separated IDs. */}
                <Typography
                  variant="caption"
                  sx={{ color: alpha(lightText, 0.4) }}
                >
                  ·
                </Typography>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha(lightText, 0.7), fontWeight: 500 }}
                  >
                    Ext: {effectiveExternalId || "—"}
                  </Typography>
                  {/* Edit pencil only for callers who can also see the raw value.
                      Users without can_reveal_pii would otherwise edit a masked
                      value blindly. */}
                  {canRevealPii && (
                    <Tooltip
                      title={t("patient.edit_external_id_title", "Edit External ID")}
                      arrow
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        onClick={() => setEditExtOpen(true)}
                        aria-label={t("patient.edit_external_id_title", "Edit External ID")}
                        sx={{
                          p: 0.25,
                          color: alpha(lightText, 0.6),
                          "&:hover": { color: lightText, backgroundColor: alpha(lightText, 0.12) },
                        }}
                      >
                        <EditOutlined sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {patientId && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: alpha(lightText, 0.4) }}
                    >
                      ·
                    </Typography>
                    <Box
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(lightText, 0.85),
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 220,
                        }}
                      >
                        {effectiveEnvNames && effectiveEnvNames.length > 0
                          ? effectiveEnvNames.join(", ")
                          : "—"}
                      </Typography>
                      <Tooltip
                        title={t(
                          "patient.edit_environments_title",
                          "Patient environments"
                        )}
                        arrow
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          onClick={() => setEditEnvOpen(true)}
                          aria-label={t(
                            "patient.edit_environments_title",
                            "Patient environments"
                          )}
                          sx={{
                            p: 0.25,
                            color: alpha(lightText, 0.6),
                            "&:hover": {
                              color: lightText,
                              backgroundColor: alpha(lightText, 0.12),
                            },
                          }}
                        >
                          <EditOutlined sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>

              {/* Proposal: a chip row saying what this record IS — which
                  cancer domains it carries, and when the row last changed.
                  Deliberately NOT the concept's green "Active Record": there
                  is no is_active (or archive) field on Patient, so that chip
                  would be decoration asserting a fact the data does not hold.
                  "Updated" is Patient.updated_at, which is auto_now — the row
                  write time, not a clinical event — hence the neutral word. */}
              {(domainChips.length > 0 || patientUpdatedAt) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                    mt: 0.75,
                  }}
                >
                  {domainChips.map((label) => (
                    <Box
                      key={label}
                      sx={{
                        px: 1.25,
                        py: 0.25,
                        borderRadius: 999,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        // Translucent, not a solid slab: the reference chip is
                        // a tinted pill you can see the surface through, and a
                        // 92% fill with deepNavy ink read as a sticker.
                        color: athenaColors.white,
                        backgroundColor: alpha(athenaColors.softPurple, 0.3),
                        border: `1px solid ${alpha(athenaColors.lightPurple, 0.5)}`,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                  {patientUpdatedAt && (
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.25,
                        borderRadius: 999,
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        color: alpha(lightText, 0.85),
                        backgroundColor: alpha(lightText, 0.1),
                        border: `1px solid ${alpha(lightText, 0.22)}`,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      {t("patient.header_updated", "Updated")}{" "}
                      {new Date(patientUpdatedAt).toLocaleDateString()}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

        </Box>

        {/* Right column: featured actions (top row) + utility buttons (bottom row) */}
        {(featuredActions || actionButtons) && (
          <Box
            sx={{
              display: "flex",
              // Single row (was a 2-row column) so featured + utility buttons
              // share one line — shorter header. Wraps only when the column is
              // genuinely too narrow to fit them.
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {featuredActions}
            {actionButtons && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {actionButtons}
              </Box>
            )}
          </Box>
        )}

        {/* Timeline Popper — appears on hovering the patient name, lazy-loaded */}
        <Popper
          open={timelineHover}
          anchorEl={nameRef.current}
          placement="bottom-start"
          transition
          sx={{ zIndex: 1300, width: cardRef.current?.offsetWidth || 320 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Box
                onMouseEnter={handlePopperEnter}
                onMouseLeave={handlePopperLeave}
                sx={{
                  mt: 0.5,
                  px: 2,
                  py: 1,
                  borderRadius: theme.shape.borderRadius / 6,
                  backgroundColor: alpha(
                    theme.palette.background.paper,
                    theme.custom?.containerOpacity?.light || 0.6
                  ),
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  boxShadow: `0 8px 32px ${alpha(
                    theme.palette.primary.dark,
                    0.18
                  )}`,
                }}
              >
                {timelineLoaded && (
                  <Suspense
                    fallback={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 0.5,
                        }}
                      >
                        <CircularProgress size={14} />
                      </Box>
                    }
                  >
                    <PatientJourneyTimeline
                      domains={tablesData.domains || {}}
                      hideLegend
                      dateOfDeath={tablesData.universal?.patientProfile?.[0]?.date_of_death as string | undefined}
                      causeOfDeath={tablesData.universal?.patientProfile?.[0]?.cause_of_death as string | undefined}
                    />
                  </Suspense>
                )}
              </Box>
            </Fade>
          )}
        </Popper>
      </Box>
      <ExternalIdEditModal
        open={editExtOpen}
        onClose={() => setEditExtOpen(false)}
        patientId={patientId ?? ""}
        currentValue={effectiveExternalId}
        onSaved={(v) => setExtOverride(v)}
      />
      {patientId && (
        <PatientEnvironmentsModal
          open={editEnvOpen}
          onClose={() => setEditEnvOpen(false)}
          patientId={patientId}
          onChanged={(names) => setEnvOverride(names)}
        />
      )}
    </Box>
  );
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DataPanel: React.FC<DataPanelProps> = ({
  patientId,
  anonymizedName,
  fullName,
  environmentNames,
  patientUpdatedAt,
  patientDomains,
  externalId,
  canRevealPii,
  legacyId,
  domainTypes,
  selectedDomains,
  tablesData,
  loading = false,
  isAIExtracting = false,
  onTablesDataChange,
  onDomainAdd,
  onRecordCreated,
  isStaleProcessing = false,
  hasExtractionErrors = false,
  onRetryExtraction,
  scrollToSection,
  externalForceVisibleTables,
  inlineActions,
  sidebarMode = false,
  eventFocus = null,
  headerActions,
  onGenerateReport,
  categoryModal = null,
  onCloseCategoryModal,
  onActiveSectionChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tableMaxHeight = sidebarMode ? MAX_TABLE_HEIGHT * 3 : MAX_TABLE_HEIGHT;

  // C6: Diagnoses/Procedures are universal singletons rendered UNDER the patient's
  // primary (first) cancer domain rather than as separate top-level sections. This
  // is the domain they nest into. Null when the patient has no cancer domain, in
  // which case they fall back to a top-level render so data is never hidden.
  // XXMM-synced patients carry the extraction-pipeline domain key
  // `medical.cancer.skin` in `patient.domains`, whereas the registry / domain-types
  // key is the un-prefixed `cancer.skin`. Both end up in `selectedDomains`, so
  // without normalization the MAIN content panel renders TWO sections for the same
  // cancer (one raw `medical.cancer.skin`, one canonical). Collapse the
  // `medical.`-prefixed alias onto its canonical registry key and dedupe — the same
  // normalization the sidebar (PatientDetailsLayout) applies. DISPLAY-ONLY: does not
  // mutate or persist `selectedDomains` (whose source merge auto-POSTs domains back
  // to the patient — a write side effect to avoid).
  const canonicalDomains = useMemo(() => {
    const domainValueSet = new Set(domainTypes.map((d) => d.value));
    const canonicalizeDomainKey = (key: string): string => {
      if (domainValueSet.has(key)) return key;
      if (key.startsWith("medical.")) {
        const stripped = key.slice("medical.".length);
        if (domainValueSet.has(stripped)) return stripped;
      }
      return key;
    };
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of selectedDomains) {
      const canon = canonicalizeDomainKey(raw);
      if (!seen.has(canon)) {
        seen.add(canon);
        out.push(canon);
      }
    }
    return out;
  }, [selectedDomains, domainTypes]);

  const primaryDomain = canonicalDomains[0] ?? null;

  // Human label of the patient's primary cancer (e.g. "Skin Cancer"), resolved
  // from the available domain types. Used for the cancer-group divider heading
  // that mirrors the sidebar's per-cancer domain heading (#15).
  const cancerDomainLabel = primaryDomain
    ? domainTypes.find((dt) => dt.value === primaryDomain)?.label ?? null
    : null;

  // Cancer Profile panel header uses the GENERIC "Cancer Profile" label so it
  // agrees with the sidebar nav row (which renders t("clinical.cancer_profile")).
  // Previously a per-cancer override ("Melanoma Profile & Risk Factors" via the
  // schema's `panel_label_key`) titled the panel, which disagreed with the
  // sidebar. The per-cancer context is now carried by the cancer-group divider
  // ("Skin Cancer") that heads this panel, so a per-cancer title is redundant and
  // would only re-introduce the sidebar/panel mismatch (#15).
  const cancerProfileTitle = t("clinical.cancer_profile", "Cancer Profile");

  // The Cancer Profile panel merges TWO clinician-facing sections (design
  // "Option A"): the single-record baseline FORM ("Risk Factors & Baseline",
  // the cancerprofiletable) followed by the dated findings LIST ("Findings &
  // Surveillance", the event-style cancerobservationtable). The observation
  // series may declare its own per-cancer heading via `panel_label_key`
  // (skin → "clinical.cancer_observation_skin"), otherwise it falls back to the
  // generic "Findings & Surveillance" label — same degradation as the profile.
  const { schema: cancerObservationSchema } = useFieldSchema(
    primaryDomain ?? "",
    "cancerobservationtable"
  );
  const cancerObservationDefaultTitle = t(
    "clinical.cancer_observation",
    "Findings & Surveillance"
  );
  const cancerObservationTitle = cancerObservationSchema?.panel_label_key
    ? t(cancerObservationSchema.panel_label_key, cancerObservationDefaultTitle)
    : cancerObservationDefaultTitle;

  // Route the sidebar record focus to exactly one event table (by section key),
  // so a medication id never collides with an event id in another table.
  const focusFor = useCallback(
    (key: string) => (eventFocus?.key === key ? eventFocus : null),
    [eventFocus]
  );

  const emptyTableContainerSx = useMemo(() => ({
    mt: 1,
    borderRadius: theme.shape.borderRadius / 6,
    background: `linear-gradient(180deg,
      ${alpha(theme.palette.primary.dark, 0.1)} 0%,
      ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.55)}`,
    backdropFilter: "blur(8px)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    py: 4,
    px: 3,
    gap: 1.5,
  }), [theme]);

  const emptyChipSx = useMemo(() => ({
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 500,
    cursor: "pointer",
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    border: "1px solid transparent",
    backdropFilter: "blur(8px)",
    transition: "all 0.25s ease",
    "& .MuiChip-icon": { color: theme.palette.info.main },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.4),
      "& .MuiChip-icon": { color: theme.palette.primary.main },
    },
  }), [theme]);

  // Scroll to section + collapse others when sidebar navigation changes
  useEffect(() => {
    if (!scrollToSection) return;

    if (scrollToSection === "all") {
      // Expand every section
      setExpandedUniversal({ patientProfile: true, molecularProfile: true, cancerProfile: true, hematologicalProfile: true, trialEnrollment: true, adverseEvents: true, medications: true, diagnoses: true, procedures: true, tumorassessments: true, diseasestatuses: true, imaging: true, systemictherapies: true, labresults: true, familyHistory: true });
      setExpandedDomain((prev) => {
        const next: Record<string, boolean> = {};
        Object.keys(prev).forEach((k) => (next[k] = true));
        return next;
      });
      setExpandedSub((prev) => {
        const next: Record<string, { static: boolean; dynamic: boolean }> = {};
        Object.keys(prev).forEach((k) => (next[k] = { static: true, dynamic: true }));
        return next;
      });
      const container = document.getElementById("data-panel-scroll-container");
      container?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Collapse all, then expand only the matching section
    const universalKeys = ["patientProfile", "molecularProfile", "cancerProfile", "hematologicalProfile", "trialEnrollment", "adverseEvents", "medications", "diagnoses", "procedures", "tumorassessments", "diseasestatuses", "imaging", "systemictherapies", "labresults", "familyHistory"] as const;
    const sectionToUniversalKey: Record<string, typeof universalKeys[number]> = {
      patient_profile: "patientProfile",
      molecular_profile: "molecularProfile",
      cancer_profile: "cancerProfile",
      hematological_profile: "hematologicalProfile",
      trial_enrollment: "trialEnrollment",
      adverse_events: "adverseEvents",
      medications: "medications",
      diagnoses: "diagnoses",
      procedures: "procedures",
      tumorassessments: "tumorassessments",
      diseasestatuses: "diseasestatuses",
      imaging: "imaging",
      systemictherapies: "systemictherapies",
      labresults: "labresults",
      family_history: "familyHistory",
    };

    const matchedUniversal = sectionToUniversalKey[scrollToSection];
    if (matchedUniversal) {
      setExpandedUniversal({
        patientProfile: false, molecularProfile: false, cancerProfile: false,
        hematologicalProfile: false, trialEnrollment: false,
        adverseEvents: false, medications: false,
        diagnoses: false, procedures: false, tumorassessments: false,
        diseasestatuses: false, imaging: false, systemictherapies: false,
        labresults: false, familyHistory: false,
        [matchedUniversal]: true,
      });
      // C6: Diagnoses/Procedures/Staging render nested inside the primary cancer
      // domain, so navigating to them must keep that domain expanded (collapsing it
      // would hide the nested block). Every other universal section stays top-level
      // and collapses all domains as before.
      const nestedUnderPrimary =
        matchedUniversal === "diagnoses" ||
        matchedUniversal === "procedures" ||
        matchedUniversal === "tumorassessments" ||
        matchedUniversal === "diseasestatuses" ||
        matchedUniversal === "imaging" ||
        matchedUniversal === "systemictherapies" ||
        matchedUniversal === "labresults";
      setExpandedDomain((prev) => {
        const next: Record<string, boolean> = {};
        Object.keys(prev).forEach(
          (k) => (next[k] = nestedUnderPrimary && k === primaryDomain)
        );
        return next;
      });
    } else {
      // May be "domainKey" (top-level), "domainKey_static" or "domainKey_event" (sub-section)
      let domainKey = scrollToSection;
      let subType: "static" | "dynamic" | null = null;

      if (scrollToSection.endsWith("_static")) {
        domainKey = scrollToSection.slice(0, -"_static".length);
        subType = "static";
      } else if (scrollToSection.endsWith("_event")) {
        domainKey = scrollToSection.slice(0, -"_event".length);
        subType = "dynamic";
      }

      setExpandedUniversal({ patientProfile: false, molecularProfile: false, cancerProfile: false, hematologicalProfile: false, trialEnrollment: false, adverseEvents: false, medications: false, diagnoses: false, procedures: false, tumorassessments: false, diseasestatuses: false, imaging: false, systemictherapies: false, labresults: false, familyHistory: false });
      setExpandedDomain((prev) => {
        const next: Record<string, boolean> = {};
        Object.keys(prev).forEach((k) => (next[k] = k === domainKey));
        return next;
      });
      setExpandedSub((prev) => {
        const next: Record<string, { static: boolean; dynamic: boolean }> = {};
        Object.keys(prev).forEach((k) => {
          if (k === domainKey) {
            next[k] = subType === "static"
              ? { static: true, dynamic: false }
              : subType === "dynamic"
                ? { static: false, dynamic: true }
                : { static: true, dynamic: true };
          } else {
            next[k] = { static: false, dynamic: false };
          }
        });
        return next;
      });
    }

    // Scroll after a short delay to let the expand animation start
    // Sub-sections need longer since the parent Collapse must open first.
    // C6: diagnoses/procedures are now nested inside the primary domain, so they
    // also need the parent-domain Collapse to open first — treat them as sub.
    const isSubSection =
      scrollToSection.endsWith("_static") ||
      scrollToSection.endsWith("_event") ||
      scrollToSection === "diagnoses" ||
      scrollToSection === "procedures" ||
      scrollToSection === "tumorassessments" ||
      scrollToSection === "diseasestatuses" ||
      scrollToSection === "imaging" ||
      scrollToSection === "systemictherapies" ||
      scrollToSection === "labresults";
    setTimeout(() => {
      const el = document.getElementById(`data-section-${scrollToSection}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, isSubSection ? 280 : 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToSection]);

  // -------------------------------------------------------------------------
  // hasLoadedOnce: flips true after the first successful (non-loading) render.
  // Used to suppress the domain loading skeleton on re-fetches triggered by
  // onRecordCreated — only the very first page load should show skeletons.
  // -------------------------------------------------------------------------
  const hasLoadedOnceRef = useRef(false);
  useEffect(() => {
    if (!loading) hasLoadedOnceRef.current = true;
  }, [loading]);

  // Reset when patient changes
  useEffect(() => {
    hasLoadedOnceRef.current = false;
  }, [patientId]);

  // -------------------------------------------------------------------------
  // Force-visible tables: tables just created via (+) are kept visible even
  // when all fields are null/empty, until the user navigates away.
  // Key format: "patientprofiletable" (universal) or "domain.key:statictable"
  // -------------------------------------------------------------------------
  const [forceVisibleTables, setForceVisibleTables] = useState<Set<string>>(
    new Set()
  );

  // Clear force-visible set when navigating to a different patient
  useEffect(() => {
    setForceVisibleTables(new Set());
  }, [patientId]);

  // Merge internal + external force-visible sets
  const mergedForceVisible = useMemo(() => {
    if (!externalForceVisibleTables || externalForceVisibleTables.size === 0) return forceVisibleTables;
    const merged = new Set(forceVisibleTables);
    externalForceVisibleTables.forEach((k) => merged.add(k));
    return merged;
  }, [forceVisibleTables, externalForceVisibleTables]);

  // ---------------------------------------------------------------------------
  // Category modal — opened from sidebar "+" on a specific field category
  // ---------------------------------------------------------------------------
  const SECTION_TO_TABLE: Record<string, { domainKey: string; tableType: string }> = useMemo(() => ({
    patient_profile: { domainKey: "cancer.skin", tableType: "patientprofiletable" },
    molecular_profile: { domainKey: "cancer.skin", tableType: "molecularprofiletable" },
    cancer_profile: { domainKey: "cancer.skin", tableType: "cancerprofiletable" },
    hematological_profile: { domainKey: "cancer.skin", tableType: "hematologicalprofiletable" },
    trial_enrollment: { domainKey: "cancer.skin", tableType: "trialenrollmenttable" },
    // Event-style universal tables (ClinicalEventTable: multiple rows per
    // patient). They live at a top-level endpoint like the profile tables, but
    // their sidebar "+" is routed to the CREATION-modal flow (create a NEW
    // record) — NOT the category modal, which edits the singleton rows[0]. The
    // routing effect below consumes these entries; catModalResolved skips them.
    adverse_events: { domainKey: "cancer.skin", tableType: "adverseeventtable" },
    medications: { domainKey: "cancer.skin", tableType: "medicationstatementtable" },
    diagnoses: { domainKey: "cancer.skin", tableType: "diagnosistable" },
    procedures: { domainKey: "cancer.skin", tableType: "procedurestable" },
    tumorassessments: { domainKey: "cancer.skin", tableType: "tumorassessmenttable" },
    diseasestatuses: { domainKey: "cancer.skin", tableType: "diseasestatustable" },
    imaging: { domainKey: "cancer.skin", tableType: "imagingtable" },
    systemictherapies: { domainKey: "cancer.skin", tableType: "systemictherapytable" },
    labresults: { domainKey: "cancer.skin", tableType: "labresulttable" },
    family_history: { domainKey: "cancer.skin", tableType: "familyhistorytable" },
  }), []);

  const catModalResolved = useMemo(() => {
    if (!categoryModal) return null;
    const { sectionId, categoryName } = categoryModal;

    let domainKey: string;
    let tableType: string;

    const universalEntry = SECTION_TO_TABLE[sectionId];
    if (universalEntry) {
      domainKey = universalEntry.domainKey;
      tableType = universalEntry.tableType;
    } else if (sectionId.endsWith("_static")) {
      domainKey = sectionId.replace(/_static$/, "").replace(/_/g, ".");
      tableType = "statictable";
    } else {
      return null;
    }

    // Event-style universal tables (ClinicalEventTable: many rows/patient) are
    // NOT resolved to the category modal — that modal edits the singleton
    // rows[0], which for these tables would no-op (0 rows) or overwrite the
    // first record (N rows). A dedicated effect routes their sidebar "+" to the
    // creation-modal flow (create-a-NEW-record) instead. Profile tables (single
    // record) keep the edit-the-singleton category modal below.
    if (EVENT_STYLE_UNIVERSAL_KINDS.includes(tableType)) return null;

    const universalDataMap: Record<string, TableRow[] | undefined> = {
      patientprofiletable: tablesData.universal?.patientProfile,
      molecularprofiletable: tablesData.universal?.molecularProfile,
      cancerprofiletable: tablesData.universal?.cancerProfile,
      hematologicalprofiletable: tablesData.universal?.hematologicalProfile,
      trialenrollmenttable: tablesData.universal?.trialEnrollment,
      // (adverse events / medications are event-style — handled above, never here)
    };

    const rows = universalDataMap[tableType] ?? tablesData.domains[domainKey]?.static;
    const row = rows?.[0];
    const rowId = row?.id ?? row?.event_id;

    return {
      domainKey,
      tableType,
      categoryName,
      row: row ?? {},
      rowId: rowId ?? 0,
    };
  }, [categoryModal, SECTION_TO_TABLE, tablesData]);

  const catModalSchema = useFieldSchema(
    catModalResolved?.domainKey ?? "",
    catModalResolved?.tableType ?? ""
  );

  const catModalFieldGroups = useFieldGroups(
    catModalResolved?.domainKey ?? "",
    catModalResolved?.tableType ?? ""
  );

  const handleCatModalSave = useCallback(
    async (fieldName: string, value: any) => {
      if (!catModalResolved || !catModalResolved.rowId) return;
      const { domainKey, tableType, rowId } = catModalResolved;
      // Every table routed through the category modal is a top-level universal
      // table (patient/molecular/hematological/trial profiles + adverse events
      // + medications) except a per-domain "statictable", which is nested under
      // its domain URL. Derive from that single distinction so newly added
      // universal tables never regress to the wrong endpoint.
      const isUniversal = tableType !== "statictable";
      const urlDomain = domainKey.replace(/\./g, "-");
      const endpoint = isUniversal ? `/${tableType}` : `/${urlDomain}-${tableType}`;
      await updateData(`${endpoint}/${rowId}/`, { [fieldName]: value });
      onTablesDataChange?.(domainKey, "static", []);
      onRecordCreated?.();
    },
    [catModalResolved, onTablesDataChange, onRecordCreated]
  );

  // Toggle a single field's lock from the category modal (cursor_logs/039).
  const handleCatModalToggleFieldLock = useCallback(
    async (fieldName: string, nextLocked: boolean) => {
      if (!catModalResolved || !catModalResolved.rowId) return;
      const { domainKey, tableType, rowId, row } = catModalResolved;
      // Every table routed through the category modal is a top-level universal
      // table (patient/molecular/hematological/trial profiles + adverse events
      // + medications) except a per-domain "statictable", which is nested under
      // its domain URL. Derive from that single distinction so newly added
      // universal tables never regress to the wrong endpoint.
      const isUniversal = tableType !== "statictable";
      const urlDomain = domainKey.replace(/\./g, "-");
      const endpoint = isUniversal ? `/${tableType}` : `/${urlDomain}-${tableType}`;
      const current: string[] = ((row as Record<string, any>)?.locked_fields as string[]) || [];
      const newLocked = nextLocked
        ? Array.from(new Set([...current, fieldName]))
        : current.filter((f) => f !== fieldName);
      await updateData(`${endpoint}/${rowId}/`, { locked_fields: newLocked });
      onTablesDataChange?.(domainKey, "static", []);
      onRecordCreated?.();
    },
    [catModalResolved, onTablesDataChange, onRecordCreated]
  );

  const handleTableCreated = useCallback(
    (info: { domainKey: string; tableType: string; isUniversal: boolean }) => {
      const key = info.isUniversal
        ? info.tableType // e.g. "patientprofiletable"
        : `${info.domainKey}:${info.tableType}`; // e.g. "medical.cancer.skin:statictable"
      setForceVisibleTables((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Creation modal — opened from empty-state "Create record" chips.
  // No DB record is created until the user actually saves a field value.
  // ---------------------------------------------------------------------------
  const [creationModal, setCreationModal] = useState<{
    open: boolean;
    domainKey: string;
    tableType: string;
    endpoint: string;
    title: string;
    isUniversal: boolean;
  } | null>(null);

  const creationRecordIdRef = useRef<number | string | null>(null);
  // Holds the in-flight "create the record" POST so that two fields committing
  // near-simultaneously (the modal fires onSave per field) can't each POST a
  // fresh record. The first caller creates + stashes the promise; concurrent
  // callers await it, then PATCH onto the resolved id.
  const creationInFlightRef = useRef<Promise<number | string | null> | null>(null);

  const creationSchema = useFieldSchema(
    creationModal?.domainKey ?? "",
    creationModal?.tableType ?? ""
  );
  const creationFieldGroups = useFieldGroups(
    creationModal?.domainKey ?? "",
    creationModal?.tableType ?? ""
  );

  const handleCreationSave = useCallback(
    async (fieldName: string, value: any) => {
      if (!creationModal || !patientId) return;
      const { endpoint, domainKey, tableType, isUniversal } = creationModal;

      if (!creationRecordIdRef.current) {
        // First save creates the record (with this field). Serialize so a
        // second field committing before the POST resolves waits for the same
        // create instead of POSTing a duplicate.
        if (!creationInFlightRef.current) {
          creationInFlightRef.current = (async () => {
            const res = await createData<Record<string, any>, any>(endpoint, {
              patient: patientId,
              [fieldName]: value,
            });
            const newId = res.id ?? res.event_id;
            creationRecordIdRef.current = newId;
            handleTableCreated({ domainKey, tableType, isUniversal });
            // Reflect the freshly created row in the background table right away
            // (pending state), instead of waiting for the modal to close. For
            // medications this also kicks off the bounded resolver follow-up so
            // resolved codes appear live. handleCreationClose still fires a final
            // refresh to pick up any fields edited afterwards in the modal.
            onRecordCreated?.();
            return newId;
          })();
          // This field's value is part of the create payload — nothing more.
          await creationInFlightRef.current;
          return;
        }
        // Another field is already creating the record — wait for its id, then
        // PATCH this field onto it below.
        await creationInFlightRef.current;
      }
      await updateData(
        `${endpoint}${creationRecordIdRef.current}/`,
        { [fieldName]: value }
      );
    },
    [creationModal, patientId, handleTableCreated, onRecordCreated]
  );

  // In-table detail-modal edits of universal event-style tables (medications,
  // adverse events, future procedures/diagnosis) update the EditableTable's own
  // rows immediately. Medications additionally re-run an async terminology
  // resolver server-side. `onRecordCreated → refreshTablesData` does an immediate
  // refetch (so the source-of-truth rows reflect any create/edit without a manual
  // reload) and, ONLY when a row is still `pending`, schedules bounded follow-ups
  // to pull the async-resolved codes in live. We must refetch on EVERY change, not
  // just when pending: native-choice tables (Staging, Disease Status, Imaging) have
  // no terminology resolver, so gating on `pending` left their edits/adds stale
  // until a page reload (coded tables happened to work only because they always go
  // pending). Always refetch; the pending-only follow-ups stay inside
  // refreshTablesData. (Joan 2026-07-16.)
  const handleUniversalEventDataChange = useCallback(
    () => {
      onRecordCreated?.();
    },
    [onRecordCreated]
  );

  const handleCreationClose = useCallback(() => {
    if (creationRecordIdRef.current) {
      onRecordCreated?.();
    }
    creationRecordIdRef.current = null;
    creationInFlightRef.current = null;
    setCreationModal(null);
  }, [onRecordCreated]);

  const openCreationModal = useCallback(
    (opts: { domainKey: string; tableType: string; endpoint: string; title: string; isUniversal: boolean }) => {
      creationRecordIdRef.current = null;
      creationInFlightRef.current = null;
      setCreationModal({ open: true, ...opts });
    },
    []
  );

  // Sidebar category "+" routing for EVENT-STYLE universal tables (Medications,
  // Adverse Events — ClinicalEventTable, many rows/patient): open the
  // creation-modal flow (create a NEW record) instead of the category modal,
  // which is built for single-record profile tables and edits rows[0]. Profile
  // tables (patient/molecular/hematological/trial) stay on the category modal —
  // catModalResolved already returns null for the event-style kinds, so this is
  // the only place their sidebar "+" is handled. See EVENT_STYLE_UNIVERSAL_KINDS.
  useEffect(() => {
    if (!categoryModal) return;
    const entry = SECTION_TO_TABLE[categoryModal.sectionId];
    if (!entry || !EVENT_STYLE_UNIVERSAL_KINDS.includes(entry.tableType)) return;
    const title =
      entry.tableType === "medicationstatementtable"
        ? t("data_panel.medications", "Medications")
        : entry.tableType === "diagnosistable"
        ? t("data_panel.diagnoses", "Diagnoses")
        : entry.tableType === "procedurestable"
        ? t("data_panel.procedures", "Procedures")
        : entry.tableType === "tumorassessmenttable"
        ? t("data_panel.tumor_assessment", "Staging")
        : entry.tableType === "diseasestatustable"
        ? t("data_panel.disease_status", "Disease Status")
        : entry.tableType === "imagingtable"
        ? t("data_panel.imaging", "Imaging")
        : entry.tableType === "systemictherapytable"
        ? t("data_panel.systemic_therapy", "Systemic Therapy")
        : entry.tableType === "labresulttable"
        ? t("data_panel.lab_results", "Lab Results")
        : entry.tableType === "familyhistorytable"
        ? t("data_panel.family_history", "Family History")
        : t("data_panel.adverse_events", "Adverse Events");
    openCreationModal({
      domainKey: entry.domainKey,
      tableType: entry.tableType,
      endpoint: `/${entry.tableType}/`,
      title,
      isUniversal: true,
    });
    onCloseCategoryModal?.();
  }, [categoryModal, SECTION_TO_TABLE, openCreationModal, onCloseCategoryModal, t]);

  // Compute anonymized name if not provided
  const displayName =
    anonymizedName || (fullName ? anonymizeName(fullName) : t("patient.unknown", "Unknown Patient"));

  // Expanded state for universal tables (Patient Profile, Molecular Profile, Hematological Profile)
  const [expandedUniversal, setExpandedUniversal] = useState({
    patientProfile: true,
    molecularProfile: true,
    cancerProfile: true,
    hematologicalProfile: true,
    trialEnrollment: true,
    adverseEvents: true,
    medications: true,
    diagnoses: true,
    procedures: true,
    tumorassessments: true,
    diseasestatuses: true,
    imaging: true,
    systemictherapies: true,
    labresults: true,
    familyHistory: true,
  });

  // Independent expand state for the two sub-sections MERGED inside the single
  // Cancer Profile panel (design "Option A"): the baseline FORM ("Risk Factors &
  // Baseline") and the dated findings LIST ("Findings & Surveillance"). They sit
  // inside the panel's own Collapse (expandedUniversal.cancerProfile), but each
  // can be folded independently — mirroring a cancer domain's Baseline | History
  // sub-boxes. Default open.
  const [baselineExpanded, setBaselineExpanded] = useState(true);
  const [observationExpanded, setObservationExpanded] = useState(true);

  // Expanded state for domains
  const [expandedDomain, setExpandedDomain] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      selectedDomains.forEach((d) => (initial[d] = true));
      return initial;
    }
  );

  // Expanded state for subsections
  const [expandedSub, setExpandedSub] = useState<
    Record<string, { static: boolean; dynamic: boolean }>
  >(() => {
    const initial: Record<string, { static: boolean; dynamic: boolean }> = {};
    selectedDomains.forEach(
      (d) => (initial[d] = { static: true, dynamic: true })
    );
    return initial;
  });

  const universalKeyToSection: Record<string, string> = {
    patientProfile: "patient_profile",
    molecularProfile: "molecular_profile",
    cancerProfile: "cancer_profile",
    hematologicalProfile: "hematological_profile",
    trialEnrollment: "trial_enrollment",
    adverseEvents: "adverse_events",
    medications: "medications",
    diagnoses: "diagnoses",
    procedures: "procedures",
    tumorassessments: "tumorassessments",
    diseasestatuses: "diseasestatuses",
    imaging: "imaging",
    systemictherapies: "systemictherapies",
    labresults: "labresults",
    familyHistory: "family_history",
  };

  // Toggle handlers — notify sidebar when a section is expanded
  const toggleUniversal = (
    section: "patientProfile" | "molecularProfile" | "cancerProfile" | "hematologicalProfile" | "trialEnrollment" | "adverseEvents" | "medications" | "diagnoses" | "procedures" | "tumorassessments" | "diseasestatuses" | "imaging" | "systemictherapies" | "labresults" | "familyHistory"
  ) => {
    setExpandedUniversal((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      if (next[section] && onActiveSectionChange) {
        onActiveSectionChange(universalKeyToSection[section]);
      }
      return next;
    });
  };

  const toggleDomain = (domain: string) => {
    setExpandedDomain((prev) => {
      const next = { ...prev, [domain]: !prev[domain] };
      if (next[domain] && onActiveSectionChange) {
        onActiveSectionChange(domain);
      }
      return next;
    });
  };

  const toggleSub = (domain: string, section: "static" | "dynamic") =>
    setExpandedSub((prev) => ({
      ...prev,
      [domain]: {
        ...prev[domain],
        [section]: !prev[domain]?.[section],
      },
    }));

  // Auto-expand newly added domains (when selectedDomains changes after mount)
  useEffect(() => {
    setExpandedDomain((prev) => {
      const next = { ...prev };
      let changed = false;
      selectedDomains.forEach((d) => {
        if (!(d in next)) {
          next[d] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setExpandedSub((prev) => {
      const next = { ...prev };
      let changed = false;
      selectedDomains.forEach((d) => {
        if (!(d in next)) {
          next[d] = { static: true, dynamic: true };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedDomains]);

  // -------------------------------------------------------------------------
  // Navigation chip bar — tracks active section + refs for smooth scrolling
  // -------------------------------------------------------------------------
  const [activeNavSection, setActiveNavSection] = useState<string | null>(null);
  const [showTrialsPanel, setShowTrialsPanel] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const trialsJustOpenedRef = useRef(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Collapse the (already-fixed) patient header card once the tables area is
  // scrolled, handing more vertical room to the tables. rAF-throttled so the
  // scroll stays smooth; threshold avoids flicker right at the top.
  const [headerCompact, setHeaderCompact] = useState(false);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        setHeaderCompact(el.scrollTop > 24),
      );
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Measure the tables column width so a domain's Diagnosis (static) + Patient
  // Journey (event) tables can sit side by side when there's genuinely room.
  // We measure the panel (not the viewport) because it sits next to the fixed
  // sidebar, so its width is smaller than the screen.
  const [panelWidth, setPanelWidth] = useState(0);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setPanelWidth(Math.floor(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const SIDE_BY_SIDE_MIN = 1150; // px — below this, stack the two tables
  const sideBySide = panelWidth >= SIDE_BY_SIDE_MIN;

  const tableActions = useRef<Record<string, ((rowIndex?: number) => void) | null>>({});

  
  const headerAddButtonsRef = useRef<Record<string, React.ReactNode>>({});
  const [headerAddButtons, setHeaderAddButtons] = useState<Record<string, React.ReactNode>>({});
  const pendingAddButtonsRef = useRef(false);

  const registerHeaderAddButton = useCallback((key: string, node: React.ReactNode) => {
    if (headerAddButtonsRef.current[key] !== node) {
      headerAddButtonsRef.current[key] = node;
      if (!pendingAddButtonsRef.current) {
        pendingAddButtonsRef.current = true;
        queueMicrotask(() => {
          pendingAddButtonsRef.current = false;
          setHeaderAddButtons({ ...headerAddButtonsRef.current });
        });
      }
    }
  }, []);

  // Icon button style — for Diagnosis / Events sub-headers (lighter backgrounds)
  const showAllIconSx = useMemo(() => ({
    width: 24,
    height: 24,
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    border: "1px solid transparent",
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
  }), [theme]);

  // "+ Add" chip for the universal event-style section headers (Medications /
  // Adverse Events), mirroring the domain History "+ Event" chip. onDark variant
  // matches the filled GlassBox header. Creates a NEW record via the wired
  // creation-modal path (openCreationModal / handleCreationSave).
  const addRecordChipSx = useMemo(() => ({
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 500,
    cursor: "pointer",
    backgroundColor: alpha(theme.palette.common.white, 0.12),
    color: alpha(theme.palette.common.white, 0.85),
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    backdropFilter: "blur(8px)",
    transition: "all 0.25s ease",
    "& .MuiChip-icon": { color: alpha(theme.palette.common.white, 0.85) },
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.22),
      color: theme.palette.common.white,
      borderColor: alpha(theme.palette.common.white, 0.4),
      "& .MuiChip-icon": { color: theme.palette.common.white },
    },
  }), [theme]);

  // "+ Add" chip for the OUTLINED sub-headers (nested Diagnoses / Procedures next
  // to a domain's Baseline / History). Mirrors AddFieldButton / AddEventButton's
  // DEFAULT (info) variant exactly, so the manual creation-modal chip matches the
  // in-table "+ Field" / "+ Event" buttons it sits beside on the light-tinted
  // header. The onDark `addRecordChipSx` above is for the filled (dark) headers.
  const addRecordChipInfoSx = useMemo(() => ({
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 500,
    cursor: "pointer",
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    border: "1px solid transparent",
    backdropFilter: "blur(8px)",
    transition: "all 0.25s ease",
    "& .MuiChip-icon": { color: theme.palette.info.main },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.4),
      "& .MuiChip-icon": { color: theme.palette.primary.main },
    },
  }), [theme]);

  // White icon button — for GlassBox headers with royalBlue/dark backgrounds
  const showAllIconWhiteSx = useMemo(() => ({
    width: 24,
    height: 24,
    backgroundColor: alpha(theme.palette.common.white, 0.12),
    color: alpha(theme.palette.common.white, 0.85),
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.22),
      color: theme.palette.common.white,
      borderColor: alpha(theme.palette.common.white, 0.4),
    },
  }), [theme]);

  // Scroll to top when trials panel is toggled on
  useEffect(() => {
    if (showTrialsPanel && trialsJustOpenedRef.current) {
      trialsJustOpenedRef.current = false;
      setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container) {
          container.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
    }
  }, [showTrialsPanel]);

  // Set a ref for a section
  const setSectionRef = useCallback(
    (key: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[key] = el;
    },
    []
  );

  // Navigate to a section: expand it (+ its sub-sections), collapse all others, scroll
  const navigateToSection = useCallback(
    (sectionKey: string) => {
      setActiveNavSection(sectionKey);

      // Collapse ALL universal sections
      setExpandedUniversal({
        patientProfile: sectionKey === "patientProfile",
        molecularProfile: sectionKey === "molecularProfile",
        cancerProfile: sectionKey === "cancerProfile",
        hematologicalProfile: sectionKey === "hematologicalProfile",
        trialEnrollment: sectionKey === "trialEnrollment",
        adverseEvents: sectionKey === "adverseEvents",
        medications: sectionKey === "medications",
        diagnoses: sectionKey === "diagnoses",
        procedures: sectionKey === "procedures",
        tumorassessments: sectionKey === "tumorassessments",
        diseasestatuses: sectionKey === "diseasestatuses",
        imaging: sectionKey === "imaging",
        systemictherapies: sectionKey === "systemictherapies",
        labresults: sectionKey === "labresults",
        familyHistory: sectionKey === "familyHistory",
      });

      // Collapse ALL domain sections, expand only the target
      setExpandedDomain((prev) => {
        const next: Record<string, boolean> = {};
        selectedDomains.forEach((d) => (next[d] = d === sectionKey));
        // Also keep domain expanded if a universal section is selected
        Object.keys(prev).forEach((d) => {
          if (!(d in next)) next[d] = false;
        });
        return next;
      });

      // Expand sub-sections of the target domain
      if (selectedDomains.includes(sectionKey)) {
        setExpandedSub((prev) => ({
          ...prev,
          [sectionKey]: { static: true, dynamic: true },
        }));
      }

      // Scroll to the section after the Collapse animation settles (~300ms MUI default).
      // Use 0 offset so the GlassBox top edge aligns flush with the scroll viewport.
      setTimeout(() => {
        const el = sectionRefs.current[sectionKey];
        const container = scrollContainerRef.current;
        if (el && container) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const scrollOffset =
            elRect.top - containerRect.top + container.scrollTop;
          container.scrollTo({
            top: Math.max(0, scrollOffset),
            behavior: "smooth",
          });
        }
      }, 350);
    },
    [selectedDomains]
  );

  /**
   * Forward wheel events to the outer scroll container when a nested
   * scrollable section reaches its top/bottom boundary.
   * This prevents the "scroll trap" effect where the user feels stuck.
   */
  const handleNestedWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atTop = el.scrollTop <= 0 && e.deltaY < 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
    if (atTop || atBottom) {
      const parent = scrollContainerRef.current;
      if (parent) {
        parent.scrollTop += e.deltaY;
      }
    }
  }, []);

  // Keys that don't count as real clinical data (metadata + schema descriptors)
  // Aligned with EditableTable EXCLUDED_FIELDS so both checks agree
  const EXCLUDED_KEYS = new Set([
    // Row-level metadata / system fields (same as EditableTable EXCLUDED_FIELDS)
    "id",
    "event_id",
    "created_at",
    "modified_at",
    "created_by",
    "batch_id",
    "event_no",
    "text",
    "table",
    "processing_task_id",
    "processing_status",
    "patient",
    "environments",
    "platform_user",
    "is_validated",
    "progress",
    "eventtable_ptr",
    "found_with_parent",
    "institution_id",
    "dynamic_entities",
    "starter",
    "source",
    "is_locked",
    "last_ai_extraction",
    "source_documents",
    "locked_fields",
    "human_edited_fields",
    // Internal backfill/sync audit blob — never user-visible (CLAUDE.md rule).
    "migration_notes",
    // Imaging image-display wiring (P1): source-image link + resolved URL are
    // display-only, never clinical data.
    "image_document",
    "image_url",
    // Extra metadata aliases
    "patient_id",
    "updated_at",
    "domain",
    "domain_type",
    "domain_key",
    "table_type",
    // Schema descriptors (field-name columns in transposed tables)
    "field",
    "field_name",
    "field_key",
    "field_type",
    "name",
    "key",
    "label",
    "description",
    "type",
    "column",
    "column_name",
    "category",
  ]);

  /**
   * Check that a table has rows with actual clinical data.
   * For transposed tables (field/value pairs): checks the "value" column.
   * For regular tables: checks that at least one non-excluded key has a value.
   */
  const hasTableData = (rows: TableRow[] | undefined): boolean => {
    if (!rows || rows.length === 0) return false;
    return rows.some((row) => {
      // Fast-path: transposed tables with explicit "value" field
      if ("value" in row) {
        return row.value != null && row.value !== "";
      }
      // General path: any non-excluded field with a value
      return Object.entries(row).some(
        ([key, value]) =>
          !EXCLUDED_KEYS.has(key) && value != null && value !== ""
      );
    });
  };

  // Extract universal tables data — show if they have real field values OR were just created via (+)
  const hasPatientProfile =
    hasTableData(tablesData.universal?.patientProfile) ||
    mergedForceVisible.has("patientprofiletable");
  const hasMolecularProfile =
    hasTableData(tablesData.universal?.molecularProfile) ||
    mergedForceVisible.has("molecularprofiletable");
  const hasCancerProfile =
    hasTableData(tablesData.universal?.cancerProfile) ||
    mergedForceVisible.has("cancerprofiletable");
  const hasCancerObservation =
    hasTableData(tablesData.universal?.cancerObservation) ||
    mergedForceVisible.has("cancerobservationtable");
  const hasHematologicalProfile =
    hasTableData(tablesData.universal?.hematologicalProfile) ||
    mergedForceVisible.has("hematologicalprofiletable");
  const hasTrialEnrollment =
    hasTableData(tablesData.universal?.trialEnrollment) ||
    mergedForceVisible.has("trialenrollmenttable");
  const hasAdverseEvents =
    hasTableData(tablesData.universal?.adverseEvents) ||
    mergedForceVisible.has("adverseeventtable");
  const hasMedications =
    hasTableData(tablesData.universal?.medications) ||
    mergedForceVisible.has("medicationstatementtable");
  const hasDiagnoses =
    hasTableData(tablesData.universal?.diagnoses) ||
    mergedForceVisible.has("diagnosistable");
  const hasProcedures =
    hasTableData(tablesData.universal?.procedures) ||
    mergedForceVisible.has("procedurestable");
  const hasTumorAssessments =
    hasTableData(tablesData.universal?.tumorAssessments) ||
    mergedForceVisible.has("tumorassessmenttable");
  const hasDiseaseStatuses =
    hasTableData(tablesData.universal?.diseaseStatuses) ||
    mergedForceVisible.has("diseasestatustable");
  const hasImaging =
    hasTableData(tablesData.universal?.imaging) ||
    mergedForceVisible.has("imagingtable");
  const hasSystemicTherapies =
    hasTableData(tablesData.universal?.systemicTherapies) ||
    mergedForceVisible.has("systemictherapytable");
  const hasLabResults =
    hasTableData(tablesData.universal?.labResults) ||
    mergedForceVisible.has("labresulttable");
  const hasFamilyHistory =
    hasTableData(tablesData.universal?.familyHistory) ||
    mergedForceVisible.has("familyhistorytable");

  // C6: whether the primary cancer domain actually renders its grid (Baseline/
  // History). This mirrors the domain loop's render condition. When true, the
  // universal Diagnoses/Procedures nest inside that grid; when false (empty or
  // no cancer domain) they fall back to a top-level render so data isn't hidden.
  const primaryDomainData = primaryDomain ? tablesData.domains?.[primaryDomain] : undefined;
  const nestUniversalUnderCancer =
    !!primaryDomain &&
    (hasTableData(primaryDomainData?.static) ||
      hasTableData(primaryDomainData?.event) ||
      mergedForceVisible.has(`${primaryDomain}:statictable`) ||
      mergedForceVisible.has(`${primaryDomain}:eventtable`));

  // Check if ANY cancer-specific table has data (or was force-created)
  const hasAnyCancerData = selectedDomains.some((domain) => {
    const dd = tablesData.domains?.[domain];
    const hasRows =
      (dd?.static?.length ?? 0) > 0 || (dd?.event?.length ?? 0) > 0;
    const forced =
      mergedForceVisible.has(`${domain}:statictable`) ||
      mergedForceVisible.has(`${domain}:eventtable`);
    return hasRows || forced;
  });

  // Global "has any data at all" — drives empty-state vs footer placement
  const hasAnyTableData =
    hasPatientProfile ||
    hasMolecularProfile ||
    hasCancerProfile ||
    hasHematologicalProfile ||
    hasTrialEnrollment ||
    hasAdverseEvents ||
    hasMedications ||
    hasDiagnoses ||
    hasProcedures ||
    hasTumorAssessments ||
    hasDiseaseStatuses ||
    hasImaging ||
    hasSystemicTherapies ||
    hasLabResults ||
    hasFamilyHistory ||
    hasAnyCancerData;

  // Whether any cancer-domain-group section will render (Cancer Profile +
  // Diagnoses/Procedures/Staging/Disease Status/Imaging/Systemic Therapy/Lab
  // Results + the per-cancer Baseline/History). Gates the cancer-group divider
  // heading so it only appears when there is cancer-group content below it (#15).
  const hasCancerGroupContent =
    hasCancerProfile ||
    hasCancerObservation ||
    hasDiagnoses ||
    hasProcedures ||
    hasTumorAssessments ||
    hasDiseaseStatuses ||
    hasImaging ||
    hasSystemicTherapies ||
    hasLabResults ||
    hasAnyCancerData;

  // Check if there are events for the timeline
  const hasEventData = useMemo(() => {
    return selectedDomains.some((domain) => {
      const events = tablesData.domains?.[domain]?.event;
      return events && events.some((row) => row.event_date);
    });
  }, [selectedDomains, tablesData.domains]);

  // Check if there's location data for body map markers
  // (aligned with extractMarkers logic in AnatomicalBodyMap)
  const hasLocationData = useMemo(() => {
    const fromDomains = selectedDomains.some((domain) => {
      const dd = tablesData.domains?.[domain];
      if (!dd) return false;
      // Static: primary_site, tumor_location, tumor_quadrant
      const hasStatic = dd.static?.some(
        (row) => row.primary_site || row.tumor_location || row.tumor_quadrant
      );
      // Event: metastasis/recurrence events (all synonyms)
      const hasEvents = dd.event?.some((row) => {
        const et = ((row.event_type as string) || "").toLowerCase();
        return (
          et.includes("metastasis") ||
          et.includes("metastatic") ||
          et.includes("recurrence") ||
          et.includes("relapse") ||
          et.includes("progression")
        );
      });
      return hasStatic || hasEvents;
    });
    // Universal DiagnosisTable: primary rows carry primary_site, metastasis rows
    // (is_primary === false) carry metastasis_body_site / metastasis_site.
    const fromDiagnoses = (tablesData.universal?.diagnoses ?? []).some((row) =>
      row.is_primary === false
        ? row.metastasis_body_site || row.metastasis_site
        : row.primary_site
    );
    return fromDomains || fromDiagnoses;
  }, [selectedDomains, tablesData.domains, tablesData.universal?.diagnoses]);

  // Build the ordered list of visible navigation sections
  const navSections = useMemo(() => {
    const sections: {
      key: string;
      label: string;
      icon?: "profile" | "molecular" | "cancerprofile" | "hematological" | "cancer";
    }[] = [];
    if (hasPatientProfile)
      sections.push({
        key: "patientProfile",
        label: t("clinical.patient_profile", "Patient Profile"),
        icon: "profile",
      });
    if (hasMolecularProfile)
      sections.push({
        key: "molecularProfile",
        label: t("clinical.molecular_profile", "Molecular Profile"),
        icon: "molecular",
      });
    if (hasCancerProfile || hasCancerObservation)
      sections.push({
        key: "cancerProfile",
        label: t("clinical.cancer_profile", "Cancer Profile"),
        icon: "cancerprofile",
      });
    if (hasHematologicalProfile)
      sections.push({
        key: "hematologicalProfile",
        label: t("data_panel.hematological", "Hematological Profile"),
        icon: "hematological",
      });
    if (hasTrialEnrollment)
      sections.push({
        key: "trialEnrollment",
        label: t("data_panel.trial_enrollment", "Clinical Trial Enrollment"),
        icon: "profile",
      });
    if (hasAdverseEvents)
      sections.push({
        key: "adverseEvents",
        label: t("data_panel.adverse_events", "Adverse Events"),
        icon: "profile",
      });
    if (hasMedications)
      sections.push({
        key: "medications",
        label: t("data_panel.medications", "Medications"),
        icon: "profile",
      });
    if (hasFamilyHistory)
      sections.push({
        key: "familyHistory",
        label: t("data_panel.family_history", "Family History"),
        icon: "profile",
      });
    // C6: Diagnoses/Procedures are no longer top-level chips — they render nested
    // under the primary cancer domain, reachable via that domain's chip/section.
    // Iterate the canonicalized, deduped domain list so a `medical.`-prefixed alias
    // never adds a duplicate chip alongside its canonical section.
    canonicalDomains.forEach((domain) => {
      const dd = tablesData.domains?.[domain];
      const hasRows =
        (dd?.static?.length ?? 0) > 0 || (dd?.event?.length ?? 0) > 0;
      const forced =
        mergedForceVisible.has(`${domain}:statictable`) ||
        mergedForceVisible.has(`${domain}:eventtable`);
      if (hasRows || forced) {
        const domainLabel =
          domainTypes.find((dt) => dt.value === domain)?.label || domain;
        sections.push({ key: domain, label: domainLabel, icon: "cancer" });
      }
    });
    return sections;
  }, [
    hasPatientProfile,
    hasMolecularProfile,
    hasCancerProfile,
    hasCancerObservation,
    hasHematologicalProfile,
    hasTrialEnrollment,
    canonicalDomains,
    tablesData.domains,
    mergedForceVisible,
    domainTypes,
    t,
  ]);

  // C6: Diagnoses/Procedures render as sub-boxes UNDER the primary cancer domain
  // (styled like its Baseline/History), or top-level as a fallback when there is
  // no cancer domain. Same data source (tablesData.universal.*) and expand state
  // (expandedUniversal) as before — only the render location/styling change.
  const renderUniversalEventSubBox = (
    // These are wide multi-column event tables (5+ columns), so they always span
    // the FULL grid width and stack vertically — half a column overflows them
    // (unlike the narrow 2-column Baseline|History). They still render nested
    // UNDER the cancer domain; only the width differs. (Joan 2026-07-15.)
    kind: "diagnoses" | "procedures" | "tumorassessments" | "diseasestatuses" | "imaging" | "systemictherapies" | "labresults"
  ) => {
    const expanded = expandedUniversal[kind];
    const tableType =
      kind === "diagnoses"
        ? "diagnosistable"
        : kind === "procedures"
        ? "procedurestable"
        : kind === "tumorassessments"
        ? "tumorassessmenttable"
        : kind === "diseasestatuses"
        ? "diseasestatustable"
        : kind === "imaging"
        ? "imagingtable"
        : kind === "systemictherapies"
        ? "systemictherapytable"
        : "labresulttable";
    const endpoint =
      kind === "diagnoses"
        ? "/diagnosistable/"
        : kind === "procedures"
        ? "/procedurestable/"
        : kind === "tumorassessments"
        ? "/tumorassessmenttable/"
        : kind === "diseasestatuses"
        ? "/diseasestatustable/"
        : kind === "imaging"
        ? "/imagingtable/"
        : kind === "systemictherapies"
        ? "/systemictherapytable/"
        : "/labresulttable/";
    const title =
      kind === "diagnoses"
        ? t("data_panel.diagnoses", "Diagnoses")
        : kind === "procedures"
        ? t("data_panel.procedures", "Procedures")
        : kind === "tumorassessments"
        ? t("data_panel.tumor_assessment", "Staging")
        : kind === "diseasestatuses"
        ? t("data_panel.disease_status", "Disease Status")
        : kind === "imaging"
        ? t("data_panel.imaging", "Imaging")
        : kind === "systemictherapies"
        ? t("data_panel.systemic_therapy", "Systemic Therapy")
        : t("data_panel.lab_results", "Lab Results");
    const rows =
      (kind === "diagnoses"
        ? tablesData.universal?.diagnoses
        : kind === "procedures"
        ? tablesData.universal?.procedures
        : kind === "tumorassessments"
        ? tablesData.universal?.tumorAssessments
        : kind === "diseasestatuses"
        ? tablesData.universal?.diseaseStatuses
        : kind === "imaging"
        ? tablesData.universal?.imaging
        : kind === "systemictherapies"
        ? tablesData.universal?.systemicTherapies
        : tablesData.universal?.labResults) ?? EMPTY_ROWS;
    return (
      <Box
        ref={setSectionRef(kind)}
        id={`data-section-${kind}`}
        sx={{ gridColumn: "1 / -1", minWidth: 0 }}
      >
        <GlassBox
          variant="outlined"
          expanded={expanded}
          onClick={() => toggleUniversal(kind)}
          sx={{
            borderColor: alpha(theme.palette.info.main, 0.35),
            background: alpha(theme.palette.primary.dark, 0.09),
            "&:hover": { borderColor: alpha(theme.palette.info.main, 0.55) },
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ flex: 1, color: theme.palette.info.main }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
              icon={<Add sx={{ fontSize: 14 }} />}
              label={t("data_panel.add_record_short", "Add")}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openCreationModal({
                  domainKey: "cancer.skin",
                  tableType,
                  endpoint,
                  title,
                  isUniversal: true,
                });
              }}
              sx={addRecordChipInfoSx}
            />
            <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!expanded) toggleUniversal(kind);
                  tableActions.current[tableType]?.(0);
                }}
                sx={showAllIconSx}
              >
                <OpenInNew sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <ExpandToggle expanded={expanded} variant="onDark" />
          </Box>
        </GlassBox>
        <Collapse in={expanded}>
          <Box
            onWheel={handleNestedWheel}
            sx={{
              mt: 1,
              maxHeight: tableMaxHeight,
              overflowX: "auto",
              overflowY: "auto",
              borderRadius: 1,
              ...NARROW_SCROLLBAR_SX,
            }}
          >
            <EditableTable
              data={rows}
              domainKey="cancer.skin"
              tableType={tableType}
              transposed={false}
              headerButtonVariant="onDark"
              // Gate the Procedure/Staging `cancer` picker: it only disambiguates
              // when the patient has >= 2 Diagnosis rows. With <= 1 diagnosis the
              // field (label + control) is hidden entirely. Keyed by
              // reference_table_type.
              referenceCounts={
                kind === "procedures" || kind === "tumorassessments" || kind === "diseasestatuses" || kind === "imaging" || kind === "systemictherapies" || kind === "labresults"
                  ? { diagnosistable: tablesData.universal?.diagnoses?.length ?? 0 }
                  : undefined
              }
              // Surface each lesion's synced images on its Diagnosis detail modal
              // (images keep their FHIR home in the Imaging panel). The Diagnosis
              // table cross-references imaging rows client-side by their `cancer` FK.
              imagingRows={kind === "diagnoses" ? (tablesData.universal?.imaging ?? EMPTY_ROWS) : undefined}
              focusEventId={focusFor(kind)?.id ?? null}
              focusEventSection={focusFor(kind)?.section ?? null}
              focusNonce={eventFocus?.nonce ?? 0}
              onDataChange={handleUniversalEventDataChange}
              onHeaderAddButton={(node) => registerHeaderAddButton(tableType, node)}
              renderHeaderActions={({ openAllFields }) => {
                tableActions.current[tableType] = openAllFields;
                return null;
              }}
            />
          </Box>
        </Collapse>
      </Box>
    );
  };

  // ── Reordered sections (#15) ──────────────────────────────────────────────
  // To mirror the sidebar order (universal sections first, then the cancer-domain
  // group), Family History and Cancer Profile are extracted here and rendered
  // later in the Stack: Family History moves AFTER Medications (last universal),
  // and Cancer Profile moves into the cancer group, right under the cancer-name
  // divider (before Diagnoses/Procedures/…). They keep their original markup,
  // state and refs — only their render position changes.

  // Cancer-group divider heading — mirrors the sidebar's per-cancer domain
  // heading (e.g. "SKIN CANCER"). Only shown when there is cancer-group content
  // below it. Provides the per-cancer context that lets the Cancer Profile panel
  // header stay the generic, sidebar-consistent "Cancer Profile".
  const cancerGroupHeaderEl =
    cancerDomainLabel && hasCancerGroupContent ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          pt: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 16,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.secondary.main, 0.6),
            flexShrink: 0,
          }}
        />
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: alpha(theme.palette.primary.dark, 0.65),
            lineHeight: 1.2,
          }}
        >
          {cancerDomainLabel}
        </Typography>
      </Box>
    ) : null;

  const cancerProfileSectionEl = (
    <>
            {/* CANCER PROFILE — empty state (only when NEITHER the baseline nor
                the findings/surveillance series has data — otherwise the merged
                panel below renders). */}
            {!hasCancerProfile && !hasCancerObservation && scrollToSection === "cancer_profile" && (
              <Box ref={setSectionRef("cancerProfile")} id="data-section-cancer_profile">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {cancerProfileTitle}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "cancerprofiletable",
                        endpoint: "/cancerprofiletable/",
                        title: cancerProfileTitle,
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* CANCER PROFILE — one panel, TWO merged sub-sections (design
                "Option A"): the single-record baseline FORM ("Risk Factors &
                Baseline", cancerprofiletable) and the dated findings LIST
                ("Findings & Surveillance", the event-style cancerobservationtable)
                rendered BELOW it. Both live inside the same panel Collapse and
                are framed like a cancer domain's Baseline | History sub-boxes, so
                the words "static"/"event"/"table" never surface to the clinician. */}
            {(hasCancerProfile || hasCancerObservation) && (
              <Box ref={setSectionRef("cancerProfile")} id="data-section-cancer_profile">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.cancerProfile}
                  onClick={() => toggleUniversal("cancerProfile")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {cancerProfileTitle}
                  </Typography>
                  <ExpandToggle expanded={expandedUniversal.cancerProfile} variant="onDark" />
                </GlassBox>
                <Collapse in={expandedUniversal.cancerProfile}>
                  <Box
                    sx={{
                      mt: 2,
                      display: "grid",
                      gap: 2,
                      alignItems: "start",
                      // Minimal-padding card that visually groups the baseline +
                      // findings sub-sections under one panel header, so they read
                      // as one unit (mirrors the cancer domain's Baseline/History
                      // frame). Kept subtle so it frames without adding weight.
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
                      background: alpha(theme.palette.primary.dark, 0.03),
                      gridTemplateColumns: "minmax(0, 1fr)",
                    }}
                  >
                    {/* SECTION 1 — Risk Factors & Baseline (single-record form) */}
                    {hasCancerProfile && (
                      <Box>
                        <GlassBox
                          variant="outlined"
                          expanded={baselineExpanded}
                          onClick={() => setBaselineExpanded((v) => !v)}
                          sx={{
                            borderColor: alpha(theme.palette.info.main, 0.35),
                            "&:hover": { borderColor: alpha(theme.palette.info.main, 0.55) },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ flex: 1, color: theme.palette.info.main }}
                          >
                            {t("clinical.cancer_profile_section", "Risk Factors & Baseline")}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!baselineExpanded) setBaselineExpanded(true);
                                  tableActions.current["cancerprofiletable"]?.(0);
                                }}
                                sx={showAllIconSx}
                              >
                                <OpenInNew sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                            {headerAddButtons["cancerprofiletable"] && (
                              <Box onClick={(e) => e.stopPropagation()}>
                                {headerAddButtons["cancerprofiletable"]}
                              </Box>
                            )}
                            <ExpandToggle
                              expanded={baselineExpanded}
                              color={theme.palette.common.white}
                            />
                          </Box>
                        </GlassBox>
                        <Collapse in={baselineExpanded}>
                          <Box
                            onWheel={handleNestedWheel}
                            sx={{
                              mt: 1,
                              maxHeight: tableMaxHeight,
                              overflowX: "auto",
                              overflowY: "auto",
                              borderRadius: 1,
                              ...NARROW_SCROLLBAR_SX,
                            }}
                          >
                            <EditableTable
                              data={tablesData.universal?.cancerProfile ?? EMPTY_ROWS}
                              domainKey="cancer.skin"
                              tableType="cancerprofiletable"
                              transposed={true}
                              onHeaderAddButton={(node) => registerHeaderAddButton("cancerprofiletable", node)}
                              renderHeaderActions={({ openAllFields }) => {
                                tableActions.current["cancerprofiletable"] = openAllFields;
                                return null;
                              }}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* SECTION 2 — Findings & Surveillance (dated event series) */}
                    <Box
                      ref={setSectionRef("cancerObservation")}
                      id="data-section-cancer_observation"
                    >
                      <GlassBox
                        variant="outlined"
                        expanded={observationExpanded}
                        onClick={() => setObservationExpanded((v) => !v)}
                        sx={{
                          borderColor: alpha(theme.palette.info.main, 0.35),
                          // Slightly more opaque than the Baseline header so the
                          // findings list reads as the darker of the two.
                          background: alpha(theme.palette.primary.dark, 0.09),
                          "&:hover": { borderColor: alpha(theme.palette.info.main, 0.55) },
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ flex: 1, color: theme.palette.info.main }}
                        >
                          {cancerObservationTitle}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Chip
                            icon={<Add sx={{ fontSize: 14 }} />}
                            label={t("data_panel.add_record_short", "Add")}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCreationModal({
                                domainKey: "cancer.skin",
                                tableType: "cancerobservationtable",
                                endpoint: "/cancerobservationtable/",
                                title: cancerObservationTitle,
                                isUniversal: true,
                              });
                            }}
                            sx={addRecordChipInfoSx}
                          />
                          <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!observationExpanded) setObservationExpanded(true);
                                tableActions.current["cancerobservationtable"]?.(0);
                              }}
                              sx={showAllIconSx}
                            >
                              <OpenInNew sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <ExpandToggle expanded={observationExpanded} variant="onDark" />
                        </Box>
                      </GlassBox>
                      <Collapse in={observationExpanded}>
                        <Box
                          onWheel={handleNestedWheel}
                          sx={{
                            mt: 1,
                            maxHeight: tableMaxHeight,
                            overflowX: "auto",
                            overflowY: "auto",
                            borderRadius: 1,
                            ...NARROW_SCROLLBAR_SX,
                          }}
                        >
                          <EditableTable
                            data={tablesData.universal?.cancerObservation ?? EMPTY_ROWS}
                            domainKey="cancer.skin"
                            tableType="cancerobservationtable"
                            transposed={false}
                            headerButtonVariant="onDark"
                            // Gate the observation `cancer` picker: it only
                            // disambiguates when the patient has >= 2 Diagnosis
                            // rows. Keyed by reference_table_type (same as the
                            // universal event sub-boxes).
                            referenceCounts={{
                              diagnosistable: tablesData.universal?.diagnoses?.length ?? 0,
                            }}
                            focusEventId={focusFor("cancerObservation")?.id ?? null}
                            focusEventSection={focusFor("cancerObservation")?.section ?? null}
                            focusNonce={eventFocus?.nonce ?? 0}
                            onDataChange={handleUniversalEventDataChange}
                            onHeaderAddButton={(node) => registerHeaderAddButton("cancerobservationtable", node)}
                            renderHeaderActions={({ openAllFields }) => {
                              tableActions.current["cancerobservationtable"] = openAllFields;
                              return null;
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            )}
    </>
  );

  const familyHistorySectionEl = (
    <>
            {/* FAMILY HISTORY — empty state */}
            {!hasFamilyHistory && scrollToSection === "family_history" && (
              <Box ref={setSectionRef("familyHistory")} id="data-section-family_history">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.family_history", "Family History")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "familyhistorytable",
                        endpoint: "/familyhistorytable/",
                        title: t("data_panel.family_history", "Family History"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* FAMILY HISTORY (top-level, open-world — like Adverse Events / Medications) */}
            {hasFamilyHistory && (
              <Box ref={setSectionRef("familyHistory")} id="data-section-family_history">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.familyHistory}
                  onClick={() => toggleUniversal("familyHistory")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.family_history", "Family History")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.add_record_short", "Add")}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreationModal({
                          domainKey: "cancer.skin",
                          tableType: "familyhistorytable",
                          endpoint: "/familyhistorytable/",
                          title: t("data_panel.family_history", "Family History"),
                          isUniversal: true,
                        });
                      }}
                      sx={addRecordChipSx}
                    />
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.familyHistory) toggleUniversal("familyHistory");
                          tableActions.current["familyhistorytable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <ExpandToggle expanded={expandedUniversal.familyHistory} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.familyHistory}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.familyHistory ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="familyhistorytable"
                      transposed={false}
                      headerButtonVariant="onDark"
                      focusEventId={focusFor("family_history")?.id ?? null}
                      focusEventSection={focusFor("family_history")?.section ?? null}
                      focusNonce={eventFocus?.nonce ?? 0}
                      onDataChange={handleUniversalEventDataChange}
                      onHeaderAddButton={(node) => registerHeaderAddButton("familyhistorytable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["familyhistorytable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}
    </>
  );

  return (
    <Box
      data-tour="data-panel"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // No overflow:hidden here — it was clipping the floating header card's
        // shadow (top/left/bottom). The tables area + inner scroller manage
        // their own overflow, so nothing leaks out of the column.
        // Transparent outer container — GlassBoxes handle their own styling.
        // borderRadius MUST be 0: with overflow:hidden a rounded value here
        // clipped the (flush) patient header card's corners with the
        // container's own 24px radius, making them look cut/non-uniform. The
        // header card and tables area carry their own rounding.
        borderRadius: 0,
        // Horizontal padding UNCHANGED (8px) so the gap to the sidebar (first
        // column) is exactly as before. Top removed (the scroll container's
        // pt:1 gives the 8px top) and bottom removed (the scroll container
        // reserves the chat clearance, so this old 16px just doubled the gap
        // above the chat).
        px: theme.spacing(1),
        pt: 0,
        pb: 0,
      }}
    >
      {/* Patient Header Card — visualizations embedded inside */}
      <PatientHeaderCard
        displayName={displayName}
        patientId={patientId}
        environmentNames={environmentNames}
        patientUpdatedAt={patientUpdatedAt}
        patientDomains={patientDomains}
        externalId={externalId}
        canRevealPii={canRevealPii}
        legacyId={legacyId}
        tablesData={tablesData}
        hasEventData={hasEventData}
        hasLocationData={hasLocationData}
        actionButtons={headerActions}
        compact={headerCompact}
        featuredActions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {hasAnyTableData && (
              <Tooltip
                title={
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="caption" fontWeight={600}>
                      {t("data_panel.find_trials", "Find Clinical Trials")}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.7, fontSize: "0.65rem" }}>
                      {t("data_panel.find_trials_via", "via ClinicalTrials.gov (NIH)")}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowTrialsPanel((v) => {
                      if (!v) trialsJustOpenedRef.current = true;
                      return !v;
                    });
                  }}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: showTrialsPanel
                      ? `linear-gradient(180deg, ${alpha(theme.palette.secondary.dark, 0.9)} 0%, ${alpha(theme.palette.secondary.dark, 0.85)} 100%)`
                      : `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                    boxShadow: theme.custom.glassShadow,
                    transition: "all .25s ease",
                    backdropFilter: "blur(6px)",
                    "&:hover": {
                      background: `linear-gradient(180deg, ${alpha(theme.palette.secondary.dark, 1)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
                      transform: "translateY(-1px)",
                      boxShadow: `${theme.custom.glassShadow}, 0 4px 12px ${alpha(theme.palette.secondary.dark, 0.15)}`,
                    },
                  }}
                >
                  <Science sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {onGenerateReport && (
              <Tooltip title={t("action_panel.generate_report", "Generate Report")} arrow placement="top">
                <span>
                  <IconButton
                    size="small"
                    disabled={isGeneratingReport}
                    onClick={async () => {
                      setIsGeneratingReport(true);
                      try { await onGenerateReport(); } finally { setIsGeneratingReport(false); }
                    }}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                      boxShadow: theme.custom.glassShadow,
                      transition: "all .25s ease",
                      backdropFilter: "blur(6px)",
                      "&:hover": {
                        background: `linear-gradient(180deg, ${alpha(theme.palette.secondary.dark, 1)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
                        transform: "translateY(-1px)",
                        boxShadow: `${theme.custom.glassShadow}, 0 4px 12px ${alpha(theme.palette.secondary.dark, 0.15)}`,
                      },
                    }}
                  >
                    {isGeneratingReport
                      ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                      : <Description sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {patientId && onDomainAdd && onRecordCreated && (
              <AddClinicalDataButton
                patientId={patientId}
                selectedDomains={selectedDomains}
                onDomainAdd={onDomainAdd}
                onRecordCreated={onRecordCreated}
                onTableCreated={handleTableCreated}
                variant="footer"
              />
            )}
          </Box>
        }
      />

      {/* Navigation Chip Bar + action buttons — hidden in sidebar mode (sidebar
          handles navigation). In sidebar mode the inner chips AND inlineActions
          are both hidden, so without this extra guard the wrapper still rendered
          as an empty 20px (py:1.25) spacer between the header card and the first
          table — making the top gap larger than the bottom one. Only render when
          there is something to show. */}
      {hasAnyTableData && patientId && (!sidebarMode || inlineActions) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            py: 1.25,
          }}
        >
          {/* Navigation chips — only in classic (non-sidebar) layout */}
          {!sidebarMode && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 0.75,
              }}
            >
              {navSections.map((sec) => {
                const isActive = activeNavSection === sec.key;
                return (
                  <Chip
                    key={sec.key}
                    label={sec.label}
                    size="small"
                    icon={
                      sec.icon === "cancer" ? (
                        <LocalHospital sx={{ fontSize: 14 }} />
                      ) : sec.icon === "profile" ? (
                        <Person sx={{ fontSize: 14 }} />
                      ) : sec.icon === "molecular" ? (
                        <Badge sx={{ fontSize: 14 }} />
                      ) : sec.icon === "cancerprofile" ? (
                        <Fingerprint sx={{ fontSize: 14 }} />
                      ) : undefined
                    }
                    onClick={() => navigateToSection(sec.key)}
                    sx={{
                      height: 26,
                      fontSize: "0.72rem",
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.22)
                        : alpha(theme.palette.info.main, 0.1),
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.palette.info.main,
                      border: `1px solid ${
                        isActive
                          ? alpha(theme.palette.primary.main, 0.4)
                          : "transparent"
                      }`,
                      backdropFilter: "blur(8px)",
                      transition: "all 0.25s ease",
                      "& .MuiChip-icon": {
                        color: isActive
                          ? theme.palette.primary.main
                          : theme.palette.info.main,
                      },
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.18),
                        color: theme.palette.primary.main,
                        "& .MuiChip-icon": {
                          color: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Inline action buttons (sidebar layout) — Find Trials, Generate Report, Add Clinical Data are now in the header card */}
          {inlineActions && (
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
              {inlineActions}
            </Box>
          )}
        </Box>
      )}

      {/* Scrollable Tables Area - wrapped for rounded clip */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Important for flex child to respect overflow
          borderRadius: 2,
          overflow: "hidden", // Clips content to rounded border
          position: "relative",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            height: "100%",
            overflow: "auto",
            pr: 0.5,
            pt: 0.5, // Top padding so scrollbar doesn't clip
            pb: 0.5, // Bottom padding so content doesn't touch edge
            // Ultra-narrow custom scrollbar with vertical padding
            "&::-webkit-scrollbar": {
              width: 3,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
              marginTop: "8px", // offset from border-radius clip
              marginBottom: "12px", // extra offset so thumb doesn't get cut at bottom
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.18),
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.3),
            },
            // Firefox
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha(
              theme.palette.text.secondary,
              0.18
            )} transparent`,
          }}
        >
          <Stack spacing={2} sx={{ minHeight: "100%" }}>
            {/* Extraction in-progress banner (only when AI pipeline is actively running) */}
            {isAIExtracting && hasAnyTableData && !isStaleProcessing && !hasExtractionErrors && (
              <Box
                sx={{
                  // Pin the banner directly below the (already-fixed) header
                  // card so the two read as one block whenever extraction is
                  // running. top:0 sticks it to the top of the scroll viewport,
                  // which sits right under the header card.
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  // Opaque base + blur so scrolling content does not bleed
                  // through the translucent info tint while the banner is
                  // pinned. Mirrors the floating header card's glass treatment.
                  bgcolor: theme.palette.background.paper,
                  backgroundImage: `linear-gradient(${alpha(
                    theme.palette.info.main,
                    0.1
                  )}, ${alpha(theme.palette.info.main, 0.1)})`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.info.main, 0.12)}`,
                  backdropFilter: "blur(6px)",
                }}
              >
                <CircularProgress size={16} color="info" />
                <Typography variant="body2" color="info.main" fontWeight={500}>
                  {t(
                    "data_panel.extracting_in_progress",
                    "Extracting data... tables will update automatically..."
                  )}
                </Typography>
              </Box>
            )}

            {/* Stale extraction banner — shown when polling timed out */}
            {isStaleProcessing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                }}
              >
                <ErrorOutline sx={{ fontSize: 20, color: "warning.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                    {t(
                      "data_panel.extraction_stale_title",
                      "Extraction may be stuck"
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "data_panel.extraction_stale_detail",
                      "The extraction process did not complete in time. You can retry it."
                    )}
                  </Typography>
                </Box>
                {onRetryExtraction && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<RefreshOutlined />}
                    onClick={onRetryExtraction}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("data_panel.retry_extraction", "Retry")}
                  </Button>
                )}
              </Box>
            )}

            {/* C2: Extraction failed banner — shown when errors exist and nothing is processing */}
            {hasExtractionErrors && !loading && !isStaleProcessing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                  border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                }}
              >
                <ErrorOutline sx={{ fontSize: 20, color: "error.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="error.main" fontWeight={600}>
                    {t(
                      "data_panel.extraction_failed_title",
                      "Some tables failed to extract"
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "data_panel.extraction_failed_detail",
                      "One or more extractions encountered an error. You can retry them."
                    )}
                  </Typography>
                </Box>
                {onRetryExtraction && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<RefreshOutlined />}
                    onClick={onRetryExtraction}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("data_panel.retry_extraction", "Retry")}
                  </Button>
                )}
              </Box>
            )}

            {/* C3: Mixed state banner — errors + still processing */}
            {hasExtractionErrors && loading && !isStaleProcessing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                  border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                }}
              >
                <ErrorOutline sx={{ fontSize: 20, color: "warning.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                    {t(
                      "data_panel.extraction_partial_title",
                      "Extracting data (some tables had errors)"
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      "data_panel.extraction_partial_detail",
                      "Extraction is still in progress but some tables failed. You can retry after it completes."
                    )}
                  </Typography>
                </Box>
                <CircularProgress size={16} color="warning" />
              </Box>
            )}

            {/* ELIGIBLE CLINICAL TRIALS — hoisted to first position for the session */}
            {patientId && hasAnyTableData && showTrialsPanel && (
              <Box ref={setSectionRef("eligibleTrials")}>
                <EligibleTrialsPanel
                  patientId={typeof patientId === "string" ? parseInt(patientId, 10) : patientId}
                  onEnrollmentCreated={onRecordCreated}
                  autoSearch
                />
              </Box>
            )}

            {/* PATIENT PROFILE — empty state */}
            {!hasPatientProfile && scrollToSection === "patient_profile" && (
              <Box ref={setSectionRef("patientProfile")} id="data-section-patient_profile">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("clinical.patient_profile", "Patient Profile")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "patientprofiletable",
                        endpoint: "/patientprofiletable/",
                        title: t("clinical.patient_profile", "Patient Profile"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* PATIENT PROFILE (top-level, same rank as cancer sections) */}
            {hasPatientProfile && (
              <Box ref={setSectionRef("patientProfile")} id="data-section-patient_profile">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.patientProfile}
                  onClick={() => toggleUniversal("patientProfile")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("clinical.patient_profile", "Patient Profile")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.patientProfile) toggleUniversal("patientProfile");
                          tableActions.current["patientprofiletable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    {headerAddButtons["patientprofiletable"] && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {headerAddButtons["patientprofiletable"]}
                      </Box>
                    )}
                    <ExpandToggle expanded={expandedUniversal.patientProfile} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.patientProfile}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.patientProfile ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="patientprofiletable"
                      transposed={true}
                      headerButtonVariant="onDark"
                      onHeaderAddButton={(node) => registerHeaderAddButton("patientprofiletable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["patientprofiletable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* MOLECULAR PROFILE — empty state */}
            {!hasMolecularProfile && scrollToSection === "molecular_profile" && (
              <Box ref={setSectionRef("molecularProfile")} id="data-section-molecular_profile">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("clinical.molecular_profile", "Molecular Profile")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "molecularprofiletable",
                        endpoint: "/molecularprofiletable/",
                        title: t("clinical.molecular_profile", "Molecular Profile"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* MOLECULAR PROFILE (top-level, same rank as cancer sections) */}
            {hasMolecularProfile && (
              <Box ref={setSectionRef("molecularProfile")} id="data-section-molecular_profile">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.molecularProfile}
                  onClick={() => toggleUniversal("molecularProfile")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("clinical.molecular_profile", "Molecular Profile")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.molecularProfile) toggleUniversal("molecularProfile");
                          tableActions.current["molecularprofiletable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    {headerAddButtons["molecularprofiletable"] && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {headerAddButtons["molecularprofiletable"]}
                      </Box>
                    )}
                    <ExpandToggle expanded={expandedUniversal.molecularProfile} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.molecularProfile}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.molecularProfile ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="molecularprofiletable"
                      transposed={true}
                      headerButtonVariant="onDark"
                      onHeaderAddButton={(node) => registerHeaderAddButton("molecularprofiletable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["molecularprofiletable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* HEMATOLOGICAL PROFILE — empty state */}
            {!hasHematologicalProfile && scrollToSection === "hematological_profile" && (
              <Box ref={setSectionRef("hematologicalProfile")} id="data-section-hematological_profile">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.hematological", "Hematological Profile")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "hematologicalprofiletable",
                        endpoint: "/hematologicalprofiletable/",
                        title: t("data_panel.hematological", "Hematological Profile"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* HEMATOLOGICAL PROFILE (top-level, same rank as cancer sections) */}
            {hasHematologicalProfile && (
              <Box ref={setSectionRef("hematologicalProfile")} id="data-section-hematological_profile">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.hematologicalProfile}
                  onClick={() => toggleUniversal("hematologicalProfile")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.hematological", "Hematological Profile")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.hematologicalProfile) toggleUniversal("hematologicalProfile");
                          tableActions.current["hematologicalprofiletable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    {headerAddButtons["hematologicalprofiletable"] && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {headerAddButtons["hematologicalprofiletable"]}
                      </Box>
                    )}
                    <ExpandToggle expanded={expandedUniversal.hematologicalProfile} />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.hematologicalProfile}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.hematologicalProfile ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="hematologicalprofiletable"
                      transposed={true}
                      headerButtonVariant="onDark"
                      onHeaderAddButton={(node) => registerHeaderAddButton("hematologicalprofiletable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["hematologicalprofiletable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* CLINICAL TRIAL ENROLLMENT — empty state */}
            {!hasTrialEnrollment && scrollToSection === "trial_enrollment" && (
              <Box ref={setSectionRef("trialEnrollment")} id="data-section-trial_enrollment">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.trial_enrollment", "Clinical Trial Enrollment")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "trialenrollmenttable",
                        endpoint: "/trialenrollmenttable/",
                        title: t("data_panel.trial_enrollment", "Clinical Trial Enrollment"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* CLINICAL TRIAL ENROLLMENT (top-level, same rank as other universal tables) */}
            {hasTrialEnrollment && (
              <Box ref={setSectionRef("trialEnrollment")} id="data-section-trial_enrollment">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.trialEnrollment}
                  onClick={() => toggleUniversal("trialEnrollment")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.trial_enrollment", "Clinical Trial Enrollment")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.trialEnrollment) toggleUniversal("trialEnrollment");
                          tableActions.current["trialenrollmenttable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    {headerAddButtons["trialenrollmenttable"] && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {headerAddButtons["trialenrollmenttable"]}
                      </Box>
                    )}
                    <ExpandToggle expanded={expandedUniversal.trialEnrollment} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.trialEnrollment}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.trialEnrollment ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="trialenrollmenttable"
                      transposed={false}
                      headerButtonVariant="onDark"
                      onHeaderAddButton={(node) => registerHeaderAddButton("trialenrollmenttable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["trialenrollmenttable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* ADVERSE EVENTS — empty state */}
            {!hasAdverseEvents && scrollToSection === "adverse_events" && (
              <Box ref={setSectionRef("adverseEvents")} id="data-section-adverse_events">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.adverse_events", "Adverse Events")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "adverseeventtable",
                        endpoint: "/adverseeventtable/",
                        title: t("data_panel.adverse_events", "Adverse Events"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* ADVERSE EVENTS (top-level, same rank as other universal tables) */}
            {hasAdverseEvents && (
              <Box ref={setSectionRef("adverseEvents")} id="data-section-adverse_events">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.adverseEvents}
                  onClick={() => toggleUniversal("adverseEvents")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.adverse_events", "Adverse Events")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.add_record_short", "Add")}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreationModal({
                          domainKey: "cancer.skin",
                          tableType: "adverseeventtable",
                          endpoint: "/adverseeventtable/",
                          title: t("data_panel.adverse_events", "Adverse Events"),
                          isUniversal: true,
                        });
                      }}
                      sx={addRecordChipSx}
                    />
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.adverseEvents) toggleUniversal("adverseEvents");
                          tableActions.current["adverseeventtable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <ExpandToggle expanded={expandedUniversal.adverseEvents} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.adverseEvents}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.adverseEvents ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="adverseeventtable"
                      transposed={false}
                      headerButtonVariant="onDark"
                      focusEventId={focusFor("adverse_events")?.id ?? null}
                      focusEventSection={focusFor("adverse_events")?.section ?? null}
                      focusNonce={eventFocus?.nonce ?? 0}
                      onDataChange={handleUniversalEventDataChange}
                      onHeaderAddButton={(node) => registerHeaderAddButton("adverseeventtable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["adverseeventtable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* MEDICATIONS — empty state */}
            {!hasMedications && scrollToSection === "medications" && (
              <Box ref={setSectionRef("medications")} id="data-section-medications">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.medications", "Medications")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "medicationstatementtable",
                        endpoint: "/medicationstatementtable/",
                        title: t("data_panel.medications", "Medications"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* MEDICATIONS (top-level, same rank as other universal tables) */}
            {hasMedications && (
              <Box ref={setSectionRef("medications")} id="data-section-medications">
                <GlassBox
                  variant="filled"
                  expanded={expandedUniversal.medications}
                  onClick={() => toggleUniversal("medications")}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.medications", "Medications")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.add_record_short", "Add")}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreationModal({
                          domainKey: "cancer.skin",
                          tableType: "medicationstatementtable",
                          endpoint: "/medicationstatementtable/",
                          title: t("data_panel.medications", "Medications"),
                          isUniversal: true,
                        });
                      }}
                      sx={addRecordChipSx}
                    />
                    <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!expandedUniversal.medications) toggleUniversal("medications");
                          tableActions.current["medicationstatementtable"]?.(0);
                        }}
                        sx={showAllIconWhiteSx}
                      >
                        <OpenInNew sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <ExpandToggle expanded={expandedUniversal.medications} variant="onDark" />
                  </Box>
                </GlassBox>
                <Collapse in={expandedUniversal.medications}>
                  <Box
                    onWheel={handleNestedWheel}
                    sx={{
                      mt: 1,
                      maxHeight: tableMaxHeight,
                      overflowX: "auto",
                      overflowY: "auto",
                      borderRadius: 1,
                      ...NARROW_SCROLLBAR_SX,
                    }}
                  >
                    <EditableTable
                      data={tablesData.universal?.medications ?? EMPTY_ROWS}
                      domainKey="cancer.skin"
                      tableType="medicationstatementtable"
                      transposed={false}
                      headerButtonVariant="onDark"
                      focusEventId={focusFor("medications")?.id ?? null}
                      focusEventSection={focusFor("medications")?.section ?? null}
                      focusNonce={eventFocus?.nonce ?? 0}
                      onDataChange={handleUniversalEventDataChange}
                      onHeaderAddButton={(node) => registerHeaderAddButton("medicationstatementtable", node)}
                      renderHeaderActions={({ openAllFields }) => {
                        tableActions.current["medicationstatementtable"] = openAllFields;
                        return null;
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* FAMILY HISTORY — last universal section (moved after Medications
                to mirror the sidebar order). See familyHistorySectionEl (#15). */}
            {familyHistorySectionEl}

            {/* ── Cancer-domain group ── The divider heads the patient's cancer
                sections (Cancer Profile, Diagnoses, Procedures, Staging, Disease
                Status, Imaging, Systemic Therapy, Lab Results + per-cancer
                Baseline/History), mirroring the sidebar's per-cancer heading. */}
            {cancerGroupHeaderEl}

            {cancerProfileSectionEl}

            {!hasDiagnoses && scrollToSection === "diagnoses" && (
              <Box ref={setSectionRef("diagnoses")} id="data-section-diagnoses">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.diagnoses", "Diagnoses")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "diagnosistable",
                        endpoint: "/diagnosistable/",
                        title: t("data_panel.diagnoses", "Diagnoses"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* DIAGNOSES — fallback top-level render only when there is no cancer
                domain to nest under (otherwise rendered inside that domain's grid). */}
            {!nestUniversalUnderCancer && hasDiagnoses && renderUniversalEventSubBox("diagnoses")}

            {!hasProcedures && scrollToSection === "procedures" && (
              <Box ref={setSectionRef("procedures")} id="data-section-procedures">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.procedures", "Procedures")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "procedurestable",
                        endpoint: "/procedurestable/",
                        title: t("data_panel.procedures", "Procedures"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* PROCEDURES — fallback top-level render only when there is no cancer
                domain to nest under (otherwise rendered inside that domain's grid). */}
            {!nestUniversalUnderCancer && hasProcedures && renderUniversalEventSubBox("procedures")}

            {!hasTumorAssessments && scrollToSection === "tumorassessments" && (
              <Box ref={setSectionRef("tumorassessments")} id="data-section-tumorassessments">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.tumor_assessment", "Staging")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "tumorassessmenttable",
                        endpoint: "/tumorassessmenttable/",
                        title: t("data_panel.tumor_assessment", "Staging"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* STAGING (TumorAssessment) — fallback top-level render only when there
                is no cancer domain to nest under (otherwise rendered inside that
                domain's grid). */}
            {!nestUniversalUnderCancer && hasTumorAssessments && renderUniversalEventSubBox("tumorassessments")}

            {!hasDiseaseStatuses && scrollToSection === "diseasestatuses" && (
              <Box ref={setSectionRef("diseasestatuses")} id="data-section-diseasestatuses">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.disease_status", "Disease Status")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "diseasestatustable",
                        endpoint: "/diseasestatustable/",
                        title: t("data_panel.disease_status", "Disease Status"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* DISEASE STATUS — fallback top-level render only when there is no
                cancer domain to nest under (otherwise rendered inside that
                domain's grid). */}
            {!nestUniversalUnderCancer && hasDiseaseStatuses && renderUniversalEventSubBox("diseasestatuses")}

            {!hasImaging && scrollToSection === "imaging" && (
              <Box ref={setSectionRef("imaging")} id="data-section-imaging">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.imaging", "Imaging")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "imagingtable",
                        endpoint: "/imagingtable/",
                        title: t("data_panel.imaging", "Imaging"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* IMAGING — fallback top-level render only when there is no
                cancer domain to nest under (otherwise rendered inside that
                domain's grid). */}
            {!nestUniversalUnderCancer && hasImaging && renderUniversalEventSubBox("imaging")}

            {!hasSystemicTherapies && scrollToSection === "systemictherapies" && (
              <Box ref={setSectionRef("systemictherapies")} id="data-section-systemictherapies">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.systemic_therapy", "Systemic Therapy")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "systemictherapytable",
                        endpoint: "/systemictherapytable/",
                        title: t("data_panel.systemic_therapy", "Systemic Therapy"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* SYSTEMIC THERAPY — fallback top-level render only when there is no
                cancer domain to nest under (otherwise rendered inside that
                domain's grid). */}
            {!nestUniversalUnderCancer && hasSystemicTherapies && renderUniversalEventSubBox("systemictherapies")}

            {!hasLabResults && scrollToSection === "labresults" && (
              <Box ref={setSectionRef("labresults")} id="data-section-labresults">
                <GlassBox variant="filled" expanded>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {t("data_panel.lab_results", "Lab Results")}
                  </Typography>
                </GlassBox>
                <Box sx={emptyTableContainerSx}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                  >
                    {t("data_panel.empty_section_title", "No data available")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                  </Typography>
                  {patientId && onRecordCreated && (
                    <Chip
                      icon={<Add sx={{ fontSize: 14 }} />}
                      label={t("data_panel.create_record", "Create record")}
                      size="small"
                      onClick={() => openCreationModal({
                        domainKey: "cancer.skin",
                        tableType: "labresulttable",
                        endpoint: "/labresulttable/",
                        title: t("data_panel.lab_results", "Lab Results"),
                        isUniversal: true,
                      })}
                      sx={emptyChipSx}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* LAB RESULTS — fallback top-level render only when there is no
                cancer domain to nest under (otherwise rendered inside that
                domain's grid). */}
            {!nestUniversalUnderCancer && hasLabResults && renderUniversalEventSubBox("labresults")}

            {/* PROCESSING STATE: Documents uploading / extracting, no tables yet */}
            {!hasAnyTableData && loading && !isStaleProcessing && !hasExtractionErrors && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <CircularProgress size={16} color="info" />
                <Typography variant="body2" color="info.main" fontWeight={500}>
                  {t(
                    "data_panel.processing_documents",
                    "Processing documents. Clinical data will appear automatically."
                  )}
                </Typography>
              </Box>
            )}

            {/* EMPTY STATE: Show the "use the sidebar" hint only when no tables
                exist AND no domain placeholder is being rendered below. Once a
                domain is selected from the sidebar, its placeholder card becomes
                the canonical empty state for that domain, so we suppress this
                message to avoid duplicate calls-to-action. */}
            {!hasAnyTableData &&
              !loading &&
              patientId &&
              onDomainAdd &&
              onRecordCreated &&
              selectedDomains.length === 0 && (
                <AddClinicalDataButton
                  patientId={patientId}
                  selectedDomains={selectedDomains}
                  onDomainAdd={onDomainAdd}
                  onRecordCreated={onRecordCreated}
                  onTableCreated={handleTableCreated}
                  variant="empty-state"
                />
              )}

            {/* CANCER-SPECIFIC TABLES — iterate the canonicalized, deduped domain
                list so a `medical.`-prefixed alias never renders a duplicate
                section alongside its canonical one (display-only). */}
            {canonicalDomains.map((domain) => {
              const domainLabel =
                domainTypes.find((t) => t.value === domain)?.label || domain;
              const domainData = tablesData.domains?.[domain];
              // Use hasTableData (field-value check) rather than row-count so that
              // all-null records from XXMM import don't render as populated sections.
              // mergedForceVisible preserves visibility for records just created via (+).
              const hasStaticRows = hasTableData(domainData?.static);
              const hasDynamicRows = hasTableData(domainData?.event);
              const hasStatic =
                hasStaticRows ||
                mergedForceVisible.has(`${domain}:statictable`);
              const hasDynamic =
                hasDynamicRows ||
                mergedForceVisible.has(`${domain}:eventtable`);
              const showDynamic = hasDynamic || patientId;
              // Only show the skeleton on the very first page load — not on re-fetches
              // triggered by onRecordCreated (which would flash skeletons for unrelated domains).
              const isProcessing = loading && !hasLoadedOnceRef.current && !hasStatic && !hasDynamic;

              // Show loading state
              if (isProcessing) {
                return (
                  <Box key={domain}>
                    <GlassBox>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocalHospital
                          sx={{
                            fontSize: 20,
                            color: theme.palette.warning.main,
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={700}>
                          {domainLabel}
                        </Typography>
                      </Box>
                      <CircularProgress size={20} />
                    </GlassBox>
                    <Box sx={{ mt: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        height={80}
                        sx={{ borderRadius: 1, mb: 1 }}
                      />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                );
              }

              if (!hasStatic && !hasDynamic) {
                const isDomainActive = scrollToSection === domain
                  || scrollToSection === `${domain}_static`
                  || scrollToSection === `${domain}_event`;
                if (!isDomainActive) return null;

                const dashed = domain.replace(/\./g, "-");
                return (
                  <Box key={domain} ref={setSectionRef(domain)} id={`data-section-${domain}`}>
                    <GlassBox variant="filled" expanded>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {domainLabel}
                      </Typography>
                    </GlassBox>
                    <Box sx={emptyTableContainerSx}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: theme.palette.primary.dark, textAlign: "center" }}
                      >
                        {t("data_panel.empty_section_title", "No data available")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                        {t("data_panel.empty_section_hint", "Create a record to start adding clinical data to this section.")}
                      </Typography>
                      {patientId && onRecordCreated && (
                        <Stack direction="row" spacing={1}>
                          <Chip
                            icon={<Add sx={{ fontSize: 14 }} />}
                            label={t("data_panel.create_diagnosis_record", "Diagnosis record")}
                            size="small"
                            onClick={() => openCreationModal({
                              domainKey: domain,
                              tableType: "statictable",
                              endpoint: `/${dashed}-statictable/`,
                              title: `${domainLabel} — ${t("data_panel.create_diagnosis_record", "Diagnosis record")}`,
                              isUniversal: false,
                            })}
                            sx={emptyChipSx}
                          />
                          <Chip
                            icon={<Add sx={{ fontSize: 14 }} />}
                            label={t("data_panel.add_event", "Add event")}
                            size="small"
                            onClick={() => openCreationModal({
                              domainKey: domain,
                              tableType: "eventtable",
                              endpoint: `/${dashed}-eventtable/`,
                              title: `${domainLabel} — ${t("data_panel.add_event", "Add event")}`,
                              isUniversal: false,
                            })}
                            sx={emptyChipSx}
                          />
                        </Stack>
                      )}
                    </Box>
                  </Box>
                );
              }

              return (
                <Box key={domain} ref={setSectionRef(domain)} id={`data-section-${domain}`}>
                  <GlassBox
                    variant="filled"
                    expanded={expandedDomain[domain] ?? false}
                    onClick={() => toggleDomain(domain)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {domainLabel}
                      </Typography>
                    </Box>
                    <ExpandToggle expanded={expandedDomain[domain] ?? false} variant="onDark" />
                  </GlassBox>

                  <Collapse in={expandedDomain[domain] ?? false}>
                    <Box
                      sx={{
                        mt: 2,
                        display: "grid",
                        gap: 2,
                        alignItems: "start",
                        // Minimal-padding card that visually groups this cancer's
                        // Baseline + History under its domain header, so users read
                        // them as one unit. Kept subtle (thin border, faint tint)
                        // so it frames without adding weight.
                        p: 1,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.dark, 0.12)}`,
                        background: alpha(theme.palette.primary.dark, 0.03),
                        // Two columns (Baseline | History) only when both exist AND
                        // the column is wide enough; otherwise stack. The event
                        // table gets the wider share; minmax(0,…) lets each table's
                        // own horizontal scroll work instead of overflowing the grid.
                        gridTemplateColumns:
                          sideBySide && hasStatic && showDynamic
                            ? "minmax(0, 0.9fr) minmax(0, 1.1fr)"
                            : "minmax(0, 1fr)",
                      }}
                    >
                      {/* Diagnosis (Static) */}
                      {hasStatic && (
                        <Box id={`data-section-${domain}_static`}>
                          <GlassBox
                            variant="outlined"
                            expanded={expandedSub[domain]?.static ?? false}
                            onClick={() => toggleSub(domain, "static")}
                            sx={{
                              borderColor: alpha(theme.palette.info.main, 0.35),
                              "&:hover": { borderColor: alpha(theme.palette.info.main, 0.55) },
                            }}
                          >
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1, color: theme.palette.info.main }}>
                              {t("data_panel.diagnosis", "Baseline")}
                              <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.5, ml: 0.75 }}>
                                {domainLabel}
                              </Typography>
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Tooltip title={t("editable.show_all_fields", "Show all fields")} placement="top" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!expandedSub[domain]?.static) toggleSub(domain, "static");
                                    tableActions.current[`${domain}_static`]?.(0);
                                  }}
                                  sx={showAllIconSx}
                                >
                                  <OpenInNew sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                              {headerAddButtons[`${domain}_static`] && (
                                <Box onClick={(e) => e.stopPropagation()}>
                                  {headerAddButtons[`${domain}_static`]}
                                </Box>
                              )}
                              <ExpandToggle
                                expanded={expandedSub[domain]?.static ?? false}
                                color={theme.palette.common.white}
                              />
                            </Box>
                          </GlassBox>
                          <Collapse in={expandedSub[domain]?.static ?? false}>
                            <Box
                              onWheel={handleNestedWheel}
                              sx={{
                                mt: 1,
                                maxHeight: tableMaxHeight,
                                overflowX: "auto",
                                overflowY: "auto",
                                borderRadius: 1,
                                ...NARROW_SCROLLBAR_SX,
                              }}
                            >
                              <EditableTable
                                data={domainData?.static ?? EMPTY_ROWS}
                                domainKey={domain}
                                tableType="statictable"
                                transposed={true}
                                onHeaderAddButton={(node) => registerHeaderAddButton(`${domain}_static`, node)}
                                renderHeaderActions={({ openAllFields }) => {
                                  tableActions.current[`${domain}_static`] = openAllFields;
                                  return null;
                                }}
                              />
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {/* Events (Dynamic) */}
                      {showDynamic && (
                        <Box id={`data-section-${domain}_event`}>
                          <GlassBox
                            variant="outlined"
                            expanded={expandedSub[domain]?.dynamic ?? false}
                            onClick={() => toggleSub(domain, "dynamic")}
                            sx={{
                              borderColor: alpha(theme.palette.info.main, 0.35),
                              // Slightly more opaque than the Baseline header (~0.06)
                              // so History reads as the darker of the two.
                              background: alpha(theme.palette.primary.dark, 0.09),
                              "&:hover": { borderColor: alpha(theme.palette.info.main, 0.55) },
                            }}
                          >
                            <Tooltip title={t("data_panel.events_tooltip", "History (Procedures, Treatments & Follow-up)")} placement="top" arrow>
                              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, color: theme.palette.info.main }}>
                                {t("data_panel.events", "History")}
                                <Typography component="span" sx={{ fontSize: "0.65rem", fontWeight: 400, opacity: 0.45, ml: 0.5 }}>
                                  ({t("data_panel.events_subtitle", "Procedures, Treatments & Follow-up")})
                                </Typography>
                                <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.5, ml: 0.75 }}>
                                  {domainLabel}
                                </Typography>
                              </Typography>
                            </Tooltip>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {headerAddButtons[`${domain}_event`] && (
                                <Box data-add-event-domain={domain} onClick={(e) => e.stopPropagation()}>
                                  {headerAddButtons[`${domain}_event`]}
                                </Box>
                              )}
                              <ExpandToggle
                                expanded={expandedSub[domain]?.dynamic ?? false}
                                variant="onDark"
                              />
                            </Box>
                          </GlassBox>
                          <Collapse in={expandedSub[domain]?.dynamic ?? false}>
                            <Box
                              onWheel={handleNestedWheel}
                              sx={{
                                mt: 1,
                                maxHeight: tableMaxHeight,
                                overflowX: "auto",
                                overflowY: "auto",
                                borderRadius: 1,
                                ...NARROW_SCROLLBAR_SX,
                              }}
                            >
                              <EditableTable
                                data={domainData?.event ?? EMPTY_ROWS}
                                domainKey={domain}
                                tableType="eventtable"
                                patientId={patientId}
                                alwaysVisibleColumns={[
                                  "event_type",
                                  "event_date",
                                ]}
                                onDataChange={(updatedRows) => {
                                  if (onTablesDataChange) {
                                    onTablesDataChange(domain, "event", updatedRows);
                                  }
                                }}
                                onEventCreated={(newEvent) => {
                                  if (onTablesDataChange) {
                                    const currentEvents =
                                      domainData?.event ?? EMPTY_ROWS;
                                    onTablesDataChange(domain, "event", [
                                      ...currentEvents,
                                      newEvent,
                                    ]);
                                  }
                                }}
                                onHeaderAddButton={(node) => registerHeaderAddButton(`${domain}_event`, node)}
                                focusEventId={focusFor(`${domain}_event`)?.id ?? null}
                                focusEventSection={focusFor(`${domain}_event`)?.section ?? null}
                                focusNonce={eventFocus?.nonce ?? 0}
                              />
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {/* C6: Universal Diagnoses/Procedures/Staging nested under the
                          primary cancer domain, next to its Baseline/History. Single
                          set per patient, so rendered only under the first cancer
                          domain. TODO(C6): multi-cancer filter by cancer FK. */}
                      {domain === primaryDomain && hasDiagnoses && renderUniversalEventSubBox("diagnoses")}
                      {domain === primaryDomain && hasProcedures && renderUniversalEventSubBox("procedures")}
                      {domain === primaryDomain && hasTumorAssessments && renderUniversalEventSubBox("tumorassessments")}
                      {domain === primaryDomain && hasDiseaseStatuses && renderUniversalEventSubBox("diseasestatuses")}
                      {domain === primaryDomain && hasImaging && renderUniversalEventSubBox("imaging")}
                      {domain === primaryDomain && hasSystemicTherapies && renderUniversalEventSubBox("systemictherapies")}
                      {domain === primaryDomain && hasLabResults && renderUniversalEventSubBox("labresults")}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* (+) button moved to navigation chip bar above */}
          </Stack>
        </Box>
      </Box>

      {/* Category field modal — opened from sidebar "+" button */}
      {catModalResolved && catModalSchema.schema && (
        <FullFieldModal
          open={!!categoryModal}
          onClose={() => onCloseCategoryModal?.()}
          title={catModalResolved.categoryName}
          fields={catModalSchema.schema.fields}
          fieldCategories={catModalFieldGroups.fieldGroups?.field_categories}
          data={catModalResolved.row as Record<string, any>}
          rowId={catModalResolved.rowId as string | number}
          lockedFields={(catModalResolved.row as Record<string, any>)?.locked_fields || []}
          // Per-field "edited by user" badges are derived from the table kind
          // (PER_FIELD_PROVENANCE_KINDS), NOT from the presence of the lock
          // toggle below. This keeps the category modal's badges correct for the
          // universal event-style tables (adverse events, medications, trials)
          // as well as the static/profile tables. See cursor_logs/039, 040.
          perFieldProvenance={PER_FIELD_PROVENANCE_KINDS.includes(
            catModalResolved.tableType
          )}
          onToggleFieldLock={handleCatModalToggleFieldLock}
          onSave={handleCatModalSave}
          filterCategory={catModalResolved.categoryName}
        />
      )}

      {/* Creation modal — opened from empty-state chips; no DB record until first save */}
      {creationModal?.open && creationSchema.schema && (
        <FullFieldModal
          open
          onClose={handleCreationClose}
          title={creationModal.title}
          fields={creationSchema.schema.fields}
          fieldCategories={creationFieldGroups.fieldGroups?.field_categories}
          data={{}}
          rowId={0}
          onSave={handleCreationSave}
        />
      )}
    </Box>
  );
};

export default DataPanel;
